<template>
  <v-container class="d-flex justify-center">
    <v-row class="justify-center">
      <v-col cols="12" md="10" lg="8" xl="6">
        <!-- Upload Card -->
        <v-card
          outlined
          :class="{'drop-zone-dark': $vuetify.theme.dark, 'drop-zone-light': !$vuetify.theme.dark}"
          class="drop-zone text-center"
          elevation="2"
          @dragover.prevent="isDragging = true"
          @dragleave="isDragging = false"
          @drop.prevent="handleDrop"
        >
          <v-card-text class="pa-6">
            <v-icon x-large color="primary" class="mb-4">mdi-cloud-upload-outline</v-icon>
            
            <div 
              class="drop-content"
              :class="{'drop-active': isDragging, 'has-file': hasSelectedFile}"
              @click="browseFiles"
            >
              <div v-if="!hasSelectedFile">
                <h3 class="text-h5 font-weight-medium mb-2">
                  Drag & Drop your project folder or zip file
                </h3>
                <p class="text-caption text--secondary">
                  Supports folders, .zip, .7zip (Max 1GB)
                </p>
                <div class="d-flex justify-center mt-4">
                  <v-btn
                    color="primary"
                    outlined
                    rounded
                    @click.stop="browseFiles"
                    class="mr-2"
                  >
                    <v-icon left>mdi-file-zip</v-icon>
                    Zip File
                  </v-btn>
                  <v-btn
                    color="secondary"
                    outlined
                    rounded
                    @click.stop="browseFolder"
                  >
                    <v-icon left>mdi-folder</v-icon>
                    Folder
                  </v-btn>
                </div>
              </div>
              
              <div v-else class="file-info">
                <v-icon large color="primary" class="mb-2">mdi-file-check-outline</v-icon>
                <h3 class="text-h6 font-weight-medium">
                  {{ selectedFile ? selectedFile.name : 'Folder with ' + selectedFolder.length + ' files' }}
                </h3>
                <p class="text-caption text--secondary">
                  <span v-if="!selectedFile">Supports folders, .zip, .tar.gz (Max 1GB)</span>
                  <span v-else>{{ formatFileSize(selectedFile.size) }}</span>
                </p>
                <v-btn
                  small
                  color="error"
                  text
                  @click.stop="cancelUpload"
                  class="mt-2"
                >
                  <v-icon left small>mdi-close</v-icon>
                  Clear
                </v-btn>
              </div>
            </div>
          </v-card-text>
          
          <input
            ref="file"
            style="display: none"
            type="file"
            accept=".zip,.7z,.tar.gz"
            @change="onFileSelected"
          />
          
          <input
            ref="folder"
            style="display: none"
            type="file"
            webkitdirectory
            directory
            multiple
            @change="onFolderSelected"
          />
        </v-card>

        <!-- Processing State -->
        <v-expand-transition>
          <div v-if="isProcessing" class="mt-6">
            <v-card elevation="2" class="pa-4">
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-subtitle-2">Analyzing code...</span>
                <v-chip small color="primary" dark>
                  {{ Math.round(progress) }}%
                </v-chip>
              </div>
              
              <v-progress-linear
                :value="progress"
                height="12"
                color="primary"
                striped
                rounded
                class="mb-3"
              ></v-progress-linear>
              
              <div class="d-flex justify-space-between text-caption text--secondary">
                <span>{{ processedFiles }} of {{ totalFiles }} files</span>
                <span class="text-truncate pl-2" style="max-width: 60%;">
                  <v-icon x-small class="mr-1">mdi-file</v-icon>
                  {{ currentFile }}
                </span>
              </div>
              
              <div class="text-center mt-3">
                <v-btn
                  small
                  color="error"
                  outlined
                  @click="cancelAnalysis"
                  :disabled="!worker"
                >
                  <v-icon left small>mdi-stop</v-icon>
                  Cancel Analysis
                </v-btn>
              </div>
            </v-card>
          </div>
        </v-expand-transition>

        <!-- File Type Selection -->
        <v-card flat class="mt-8" elevation="2" :disabled="isProcessing">
          <v-card-title class="justify-center py-4">
            <span class="primary--text" style="font-weight: bolder; font-size: medium;">
              SELECT FILE TYPES TO ANALYZE
            </span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="px-4">
            <v-container fluid class="px-0">
              <v-row dense>
                <v-col
                  v-for="(type, index) in fileTypes"
                  :key="index"
                  cols="6"
                  sm="4"
                  md="3"
                >
                  <v-checkbox
                    v-model="selectedTypes"
                    :label="type.label"
                    :value="type.value"
                    dense
                    hide-details
                    color="primary"
                    class="mt-0"
                    :disabled="isProcessing"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-btn 
              small 
              @click="selectAll" 
              text 
              color="primary"
              :disabled="isProcessing"
            >
              Select All
            </v-btn>
            <v-btn 
              small 
              @click="deselectAll" 
              text 
              color="secondary"
              :disabled="isProcessing"
            >
              Deselect All
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- Analysis Options -->
        <v-card flat class="mt-4" elevation="2" :disabled="isProcessing">
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6">
                <v-switch
                  v-model="countEmptyLines"
                  label="Count empty lines"
                  color="primary"
                  hide-details
                  :disabled="isProcessing"
                ></v-switch>
              </v-col>
              <v-col cols="12" sm="6">
                <v-switch
                  v-model="ignoreComments"
                  label="Ignore comments"
                  color="primary"
                  hide-details
                  :disabled="isProcessing"
                ></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

<!-- Performance Options -->
<v-card flat class="mt-4" elevation="2" :disabled="isProcessing">
  <v-card-title class="justify-center py-3">
    <span class="primary--text" style="font-weight: bolder; font-size: medium;">
      PERFORMANCE OPTIONS
    </span>
  </v-card-title>
  <v-divider></v-divider>
  <v-card-text class="px-4 py-3">
    <v-row>
      <!-- Master switch - Thorough Analysis -->
      <v-col cols="12">
        <v-switch
          v-model="thoroughAnalysis"
          :label="thoroughAnalysis ? 'Thorough Analysis (Slower, more detailed)' : 'Fast Analysis (Quick results)'"
          color="primary"
          hide-details
          :disabled="isProcessing"
        >
          <template v-slot:label>
            <span class="font-weight-medium">
              <v-icon 
                :color="thoroughAnalysis ? 'error' : 'success'" 
                class="mr-2"
              >
                {{ thoroughAnalysis ? 'mdi-speedometer-slow' : 'mdi-speedometer' }}
              </v-icon>
              {{ thoroughAnalysis ? 'Thorough Analysis (Slower, more detailed)' : 'Fast Analysis (Quick results)' }}
            </span>
          </template>
        </v-switch>
      </v-col>
    </v-row>
    
    <v-row class="mt-2">
      <v-col cols="12" md="6">
        <v-switch
          v-model="analyzeDocumentation"
          label="Documentation quality"
          color="primary"
          hide-details
          :disabled="isProcessing || !thoroughAnalysis"
          :readonly="!thoroughAnalysis"
        >
          <template v-slot:label>
            <span class="text-caption" :class="{ 'grey--text': !thoroughAnalysis }">
              <v-icon 
                small 
                :color="thoroughAnalysis ? 'info' : 'grey'" 
                class="mr-1"
              >
                mdi-file-document
              </v-icon>
              Analyze docstrings and comments
            </span>
          </template>
        </v-switch>
      </v-col>
      
      <v-col cols="12" md="6">
        <v-switch
          v-model="analyzeDependencies"
          label="Dependency graph"
          color="primary"
          hide-details
          :disabled="isProcessing || !thoroughAnalysis"
          :readonly="!thoroughAnalysis"
        >
          <template v-slot:label>
            <span class="text-caption" :class="{ 'grey--text': !thoroughAnalysis }">
              <v-icon 
                small 
                :color="thoroughAnalysis ? 'warning' : 'grey'" 
                class="mr-1"
              >
                mdi-graph
              </v-icon>
              Build dependency graph (slower)
            </span>
          </template>
        </v-switch>
      </v-col>
    </v-row>
    
    <!-- Info messages based on state -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-alert
          v-if="thoroughAnalysis"
          type="info"
          dense
          outlined
          class="mb-0"
        >
          <v-icon left small>mdi-information</v-icon>
          Thorough mode enabled: Analyzing documentation quality and building dependency graph.
          <v-btn
            v-if="selectedFileSize > 1000000"
            text
            x-small
            color="warning"
            @click="thoroughAnalysis = false"
            class="ml-2"
          >
            Switch to fast mode
          </v-btn>
        </v-alert>
        
        <v-alert
          v-else
          type="success"
          dense
          outlined
          class="mb-0"
        >
          <v-icon left small>mdi-flash</v-icon>
          Fast mode: Quick analysis without documentation quality or dependency graph.
          <v-btn
            text
            x-small
            color="primary"
            @click="thoroughAnalysis = true"
            class="ml-2"
          >
            Enable thorough mode
          </v-btn>
        </v-alert>
      </v-col>
    </v-row>
    
    <!-- Large file warning -->
    <v-row v-if="selectedFileSize > 1000000 && !thoroughAnalysis" class="mt-2">
      <v-col cols="12">
        <v-chip small color="warning" outlined>
          <v-icon left small>mdi-alert</v-icon>
          Large file detected. Fast mode recommended for quicker results.
        </v-chip>
      </v-col>
    </v-row>
  </v-card-text>
</v-card>

<!-- ===== NEW: PROGRESS PERSISTENCE CONTROLS - PLACE HERE ===== -->
<v-card flat class="mt-4" elevation="2">
  <v-card-actions class="justify-end pa-2">
    <v-btn 
      small 
      text 
      color="primary" 
      @click="saveProgress"
      :disabled="!hasSelectedFile && !selectedTypes.length"
    >
      <v-icon left small>mdi-content-save</v-icon>
      Save Progress
    </v-btn>
    <v-btn 
      small 
      text 
      color="secondary" 
      @click="loadProgress"
    >
      <v-icon left small>mdi-download</v-icon>
      Load Progress
    </v-btn>
    <v-menu offset-y>
      <template v-slot:activator="{ on }">
        <v-btn icon small v-on="on">
          <v-icon small>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>
      <v-list dense>
        <v-list-item @click="clearSavedProgress">
          <v-list-item-icon>
            <v-icon small>mdi-delete</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Clear Saved Progress</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="lastSaved">
          <v-list-item-content>
            <v-list-item-title class="text-caption">
              Last saved: {{ lastSaved.toLocaleTimeString() }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-card-actions>
</v-card>

        <!-- Search Bar - Feature #3 -->
<!-- ===== UPDATED SEARCH BAR WITH INFINITE SCROLL ===== -->
<v-expand-transition>
  <div v-if="analysisComplete" class="mt-4">
    <v-card flat elevation="2">
      <v-card-text class="py-2">

        <!-- SEARCH FIELD -->
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          :label="isSearching ? 'Searching...' : 'Search files, functions, or dependencies...'"
          dense
          outlined
          clearable
          hide-details
          class="search-field"
          :loading="isSearching"
          @keyup.esc="searchQuery = ''"
        >
          <!-- SEARCH STATUS CHIPS -->
          <template v-slot:append>

            <v-chip
              v-if="isSearching"
              small
              class="mt-1"
              color="grey"
            >
              Searching...
            </v-chip>

            <v-chip
              v-else-if="searchQuery && !isSearching"
              small
              class="mt-1"
              :color="enhancedSearchResults.length > 0 ? 'primary' : 'grey'"
            >
              {{ enhancedSearchResults.length }} results
            </v-chip>

          </template>
        </v-text-field>

        <!-- INLINE SEARCH RESULTS (NO OVERLAY) -->
        <v-expand-transition>
          <v-card
            v-if="searchQuery && !isSearching"
            class="mt-3 search-results-card"
            outlined
          >
            <v-card-text class="pa-0 search-results-container">

              <v-list dense>

                <v-list-item
                  v-for="(result, index) in visibleSearchResults"
                  :key="result.name + index"
                  @click="navigateToFile(result)"
                  class="search-result-item"
                >

                  <!-- ICON -->
                  <v-list-item-icon>
                    <v-icon
                      small
                      :color="getFileIconColor(result.extension)"
                    >
                      {{ getFileIcon(result.extension) }}
                    </v-icon>
                  </v-list-item-icon>

                  <!-- CONTENT -->
                  <v-list-item-content>

                    <v-list-item-title class="search-result-title">
                      <span class="font-weight-medium">
                        {{ getFileName(result.name) }}
                      </span>

                      <v-chip
                        v-if="result.matchType"
                        x-small
                        class="ml-2"
                        :color="getMatchTypeColor(result.matchType)"
                        dark
                      >
                        {{ result.matchType }}
                      </v-chip>
                    </v-list-item-title>

                    <v-list-item-subtitle class="search-result-path text-caption">
                      <v-icon x-small class="mr-1">mdi-folder</v-icon>
                      {{ result.name.split('/').slice(0, -1).join('/') + '/' || '/' }}
                    </v-list-item-subtitle>

                    <div
                      v-if="result.matchPreview"
                      class="match-preview text-caption mt-1"
                    >
                      <span v-html="result.matchPreview"></span>
                    </div>

                  </v-list-item-content>

                  <!-- COPY BUTTON -->
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      @click.stop="copyPath(result.name)"
                    >
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </v-list-item-action>

                </v-list-item>

                <!-- LOADING MORE -->
                <div
                  v-if="hasMoreResults"
                   class="text-center py-3 scroll-trigger"
                >
                  <v-progress-circular
                    indeterminate
                    size="24"
                    color="primary"
                  />
                  <div class="text-caption mt-1">
                    Loading more...
                  </div>
                </div>

                <!-- EMPTY STATE -->
                <div
                  v-if="enhancedSearchResults.length === 0"
                  class="text-center py-6 text-caption text--secondary"
                >
                  <v-icon
                    large
                    color="grey lighten-1"
                    class="mb-2"
                  >
                    mdi-file-search-outline
                  </v-icon>
                  <div>
                    No files match "{{ searchQuery }}"
                  </div>
                </div>

              </v-list>

            </v-card-text>
          </v-card>
        </v-expand-transition>

      </v-card-text>
    </v-card>
  </div>
</v-expand-transition>

        <!-- Analyze Button -->
        <div class="text-center mt-8">
          <v-btn
            :loading="isProcessing"
            :disabled="!hasSelectedFile || selectedTypes.length === 0 || isProcessing"
            color="primary"
            large
            rounded
            depressed
            @click="analyzeCode"
            class="px-8"
          >
            <v-icon left>mdi-magnify-scan</v-icon>
            {{ isProcessing ? 'Analyzing...' : 'Analyze Code' }}
          </v-btn>
        </div>

        <!-- Results with ALL Features -->
 <!-- Results with ALL Features - FULLY RESPONSIVE -->
<v-expand-transition>
  <div v-if="analysisComplete" class="mt-8">
    <!-- Enhanced Summary - Responsive -->
    <v-card class="mb-4" elevation="2">
      <v-card-title class="justify-center primary white--text py-4">
        <v-icon left color="white">mdi-file-chart-outline</v-icon>
        ANALYSIS COMPLETE
      </v-card-title>
      <v-card-text class="text-center pt-6 pb-4">
        <div class="text-h4 text-sm-h3 primary--text font-weight-bold">
          {{ totalLines.toLocaleString() }}
        </div>
        <div class="text-subtitle-1 text--secondary">Total lines of code</div>
        
        <v-row class="mt-4">
          <v-col cols="6" sm="3">
            <div class="text-h6 text-sm-h5">{{ totalFilesProcessed }}</div>
            <div class="text-caption text--secondary">Files Analyzed</div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="text-h6 text-sm-h5">{{ processingTime }}s</div>
            <div class="text-caption text--secondary">Processing Time</div>
          </v-col>
<!-- In the Enhanced Summary section -->
<v-col cols="6" sm="3">
  <div class="text-h6 text-sm-h5">
    {{ (complexityMetrics.avgComplexity || 0).toFixed(1) }}
  </div>
  <div class="text-caption text--secondary">Avg Complexity</div>
</v-col>
          <v-col cols="6" sm="3">
            <div class="text-h6 text-sm-h5">{{ duplicates.length }}</div>
            <div class="text-caption text--secondary">Duplicates Found</div>
          </v-col>
        </v-row>
        
        <v-chip color="success" class="mt-4" dark>
          <v-icon left small>mdi-check</v-icon>
          Analysis completed with enhanced metrics
        </v-chip>
      </v-card-text>
    </v-card>

    <!-- All Tabs - Fully Responsive -->
    <v-card elevation="2">
      <!-- Responsive Tabs -->
      <v-tabs 
        v-model="activeTab" 
        show-arrows
        grow
        class="responsive-tabs"
      >
        <v-tab class="tab-item">
          <v-icon left small>mdi-format-list-bulleted-type</v-icon>
          <span class="tab-label">By Type</span>
        </v-tab>
        <v-tab class="tab-item">
          <v-icon left small>mdi-sort</v-icon>
          <span class="tab-label">Largest</span>
        </v-tab>
        <v-tab class="tab-item">
          <v-icon left small>mdi-chart-bar</v-icon>
          <span class="tab-label">Stats</span>
        </v-tab>
        <v-tab class="tab-item">
          <v-icon left small>mdi-file-tree</v-icon>
          <span class="tab-label">Structure</span>
        </v-tab>
        <!-- Feature #1 - Charts -->
        <v-tab class="tab-item">
          <v-icon left small>mdi-chart-pie</v-icon>
          <span class="tab-label">Charts</span>
        </v-tab>
        <!-- Feature #2 - Complexity -->
        <v-tab class="tab-item">
          <v-icon left small>mdi-code-braces</v-icon>
          <span class="tab-label">Complexity</span>
        </v-tab>
        <!-- Feature #4 - Duplicates -->
        <v-tab class="tab-item">
          <v-icon left small>mdi-content-copy</v-icon>
          <span class="tab-label">Duplicates</span>
        </v-tab>
        <!-- Feature #5 - Language Analysis -->
        <v-tab class="tab-item">
          <v-icon left small>mdi-code-tags</v-icon>
          <span class="tab-label">Language</span>
        </v-tab>
        <!-- Feature #6 - Dependencies -->
        <v-tab class="tab-item">
          <v-icon left small>mdi-graph</v-icon>
          <span class="tab-label">Deps</span>
        </v-tab>
        <!-- ===== ISSUES TAB ===== -->
        <v-tab class="tab-item">
          <v-icon left small>mdi-bug</v-icon>
          <span class="tab-label">Issues</span>
        </v-tab>
      </v-tabs>

      <!-- Responsive Tab Content -->
      <v-tabs-items v-model="activeTab" class="tab-content">
        <!-- File Type View - Responsive Table -->
        <v-tab-item>
          <v-card-text class="pa-2 pa-sm-4">
            <div class="responsive-table">
              <v-data-table
                :headers="typeHeaders"
                :items="fileTypeSummary"
                :items-per-page="10"
                class="elevation-1"
                hide-default-footer
                mobile-breakpoint="0"
              >
                <template v-slot:item.type="{ item }">
                  <div class="d-flex align-center">
                    <v-chip x-small color="primary" dark class="mr-2">
                      {{ getExtensionIcon(item.type) }}
                    </v-chip>
                    <span class="type-label">{{ getFileTypeLabel(item.type) }}</span>
                  </div>
                </template>
                <template v-slot:item.lines="{ item }">
                  <span class="font-weight-medium">
                    {{ item.lines.toLocaleString() }}
                  </span>
                </template>
                <template v-slot:item.percentage="{ item }">
                  <div class="d-flex align-center">
                    <v-progress-linear
                      :value="(item.lines / totalLines) * 100"
                      height="8"
                      color="primary"
                      rounded
                      class="percentage-bar"
                    ></v-progress-linear>
                    <span class="text-caption ml-2 percentage-text">
                      {{ ((item.lines / totalLines) * 100).toFixed(1) }}%
                    </span>
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-tab-item>

        <!-- Largest Files View - Responsive -->
        <v-tab-item>
          <v-card-text class="pa-2 pa-sm-4">
            <v-list two-line>
              <v-list-item
                v-for="(file, index) in largestFiles.slice(0, 15)"
                :key="file.name"
                class="largest-file-item"
              >
                <v-list-item-avatar class="rank-avatar">
                  <v-avatar :color="getRankColor(index)" size="36" class="rank-avatar-inner">
                    <span class="white--text font-weight-bold">{{ index + 1 }}</span>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium file-name">
                    {{ file.name }}
                    <v-chip x-small class="ml-2 file-extension-chip" color="grey lighten-2">
                      {{ getFileExtension(file.name) }}
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle class="file-details">
                    <span class="mr-3">
                      <v-icon x-small class="mr-1">mdi-text</v-icon>
                      {{ file.lines.toLocaleString() }} lines
                    </span>
                    <span>
                      <v-icon x-small class="mr-1">mdi-file</v-icon>
                      {{ formatFileSize(file.size) }}
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip small :color="getRankColor(index)" dark class="rank-chip">
                    #{{ index + 1 }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-tab-item>

        <!-- Statistics View - Fully Responsive -->
        <v-tab-item>
          <v-card-text class="pa-2 pa-sm-4">
            <!-- Stats Cards -->
            <v-row class="mb-4">
              <v-col cols="6" md="3" v-for="stat in statistics" :key="stat.title">
                <v-card elevation="1" class="text-center pa-2 pa-sm-3 stat-card">
                  <div class="text-h6 text-sm-h5 font-weight-bold primary--text">
                    {{ stat.value }}
                  </div>
                  <div class="text-caption text--secondary mt-1">
                    {{ stat.title }}
                  </div>
                </v-card>
              </v-col>
            </v-row>
            
            <!-- Size Distribution -->
            <v-card elevation="1" class="pa-3 pa-sm-4 mb-4">
              <div class="text-subtitle-1 font-weight-medium mb-3">
                File Size Distribution
              </div>
              <div class="size-distribution">
                <div v-for="range in sizeRanges" :key="range.label" class="size-range-item mb-2">
                  <div class="d-flex flex-wrap align-center">
                    <span class="text-caption range-label" style="min-width: 80px;">
                      {{ range.label }}
                    </span>
                    <div class="progress-container flex-grow-1" style="min-width: 100px;">
                      <v-progress-linear
                        :value="(range.count / totalFilesProcessed) * 100"
                        height="10"
                        :color="range.color"
                        rounded
                      ></v-progress-linear>
                    </div>
                    <span class="text-caption range-count ml-2" style="min-width: 70px;">
                      {{ range.count }} ({{ ((range.count / totalFilesProcessed) * 100).toFixed(1) }}%)
                    </span>
                  </div>
                </div>
              </div>
            </v-card>

<!-- Health Metrics with Overall Score -->
<v-row class="mt-4">
  <v-col cols="12">
    <v-card 
      elevation="1" 
      class="pa-3 pa-sm-4 health-metrics-section"
      :class="{ 'capturing-screenshot': isCapturingScreenshot }"
    >
<!-- Overall Health Score - Shareable -->
<div class="d-flex flex-wrap justify-space-between align-center mb-4">
  <div class="text-subtitle-1 font-weight-medium">
    Code Health Metrics
  </div>
  <div class="d-flex align-center">
    <div class="text-caption mr-2">Overall Score</div>
    <v-chip
      :color="healthScoreColor"
      dark
      large
      class="px-4"
      style="font-size: 1.2rem; font-weight: bold;"
    >
      <v-icon left>{{ healthScoreIcon }}</v-icon>
      {{ overallHealthScore }}/100
    </v-chip>
    
    <!-- Share Dropdown -->
    <v-menu
      v-model="shareMenu"
      :close-on-content-click="false"
      offset-y
      left
      nudge-bottom="8"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          small
          class="ml-2"
          v-bind="attrs"
          v-on="on"
          :disabled="!analysisComplete || isCapturingScreenshot"
          :loading="isCapturingScreenshot"
          title="Share health score"
        >
          <v-icon small>mdi-share-variant</v-icon>
        </v-btn>
      </template>
      
      <v-card min-width="200" class="share-menu">
        <v-list dense>
          <v-list-item
            v-for="platform in sharePlatforms"
            :key="platform.id"
            @click="shareToPlatform(platform.id)"
            :disabled="isCapturingScreenshot"
          >
            <v-list-item-icon>
              <v-icon :color="platform.color">{{ platform.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ platform.label }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action v-if="platform.id === 'instagram'">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon small color="grey" v-on="on">mdi-information</v-icon>
                </template>
                <span>Saves screenshot for Instagram</span>
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        
        <v-divider></v-divider>
        
        <v-card-actions class="justify-center pa-2">
          <v-btn
            small
            text
            color="primary"
            @click="shareMenu = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</div>
      
      <!-- Progress Circulars -->
      <v-row>
        <v-col cols="6" md="3" v-for="metric in healthMetrics" :key="metric.name">
          <div class="text-center health-metric">
            <v-progress-circular
              :value="metric.value"
              :color="metric.color"
              size="60"
              width="6"
              class="health-circular"
            >
              <span class="font-weight-bold">{{ metric.value }}%</span>
            </v-progress-circular>
            <div class="text-caption mt-2 metric-name font-weight-medium">{{ metric.name }}</div>
          </div>
        </v-col>
      </v-row>
      
      <!-- Health Score Breakdown -->
      <v-divider class="my-4"></v-divider>
      
      <v-row dense>
        <v-col cols="6" sm="3">
          <div class="d-flex align-center">
            <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
            <span class="text-caption">Complexity: </span>
            <span class="text-caption font-weight-bold ml-1">{{ (complexityMetrics.avgComplexity || 0).toFixed(1) }}</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="d-flex align-center">
            <v-icon small color="info" class="mr-1">mdi-content-copy</v-icon>
            <span class="text-caption">Duplicates: </span>
            <span class="text-caption font-weight-bold ml-1">{{ duplicates.length }}</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="d-flex align-center">
            <v-icon small color="warning" class="mr-1">mdi-clock-alert</v-icon>
            <span class="text-caption">TODOs: </span>
            <span class="text-caption font-weight-bold ml-1">{{ todoComments.length }}</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="d-flex align-center">
            <v-icon small color="error" class="mr-1">mdi-graph</v-icon>
            <span class="text-caption">Circular: </span>
            <span class="text-caption font-weight-bold ml-1">{{ circularDependencies.length }}</span>
          </div>
        </v-col>
      </v-row>

          <!-- ===== ADD THIS NEW ROW AFTER YOUR EXISTING BREAKDOWN ===== -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-divider class="mb-4"></v-divider>
          
          <!-- Tech Debt Summary -->
          <div class="d-flex flex-wrap align-center mb-3">
            <v-icon color="warning" class="mr-2">mdi-clock-alert</v-icon>
            <span class="text-subtitle-2 font-weight-medium">Technical Debt Summary</span>
            <v-spacer></v-spacer>
            <!-- <v-chip 
              small 
              :color="getDebtScoreColor(techDebt.debtScore)" 
              dark
              class="ml-2"
            >
              Score: {{ techDebt.debtScore }}/100
            </v-chip> -->
          </div>
          
          <v-row>
            <!-- Estimated Fix Hours -->
            <v-col cols="6" md="3">
              <div class="text-center">
                <div class="text-h6 font-weight-bold primary--text">
                  {{ techDebt.estimatedHours }}
                </div>
                <div class="text-caption text--secondary">Fix Hours</div>
              </div>
            </v-col>
            
            <!-- Stability Score -->
            <v-col cols="6" md="3">
              <div class="text-center">
                <div class="text-h6 font-weight-bold" :class="getChurnColor(codeChurn.stabilityScore)">
                  {{ codeChurn.stabilityScore }}%
                </div>
                <div class="text-caption text--secondary">Stability</div>
              </div>
            </v-col>
            
            <!-- High Churn Files -->
            <v-col cols="6" md="3">
              <div class="text-center">
                <div class="text-h6 font-weight-bold warning--text">
                  {{ codeChurn.highChurnFiles.length }}
                </div>
                <div class="text-caption text--secondary">High Churn Files</div>
              </div>
            </v-col>
            
            <!-- Top Priority Issues -->
            <v-col cols="6" md="3">
              <div class="text-center">
                <div class="text-h6 font-weight-bold error--text">
                  {{ techDebt.recommendations.filter(r => r.effort === 'high').length }}
                </div>
                <div class="text-caption text--secondary">Critical Issues</div>
              </div>
            </v-col>
          </v-row>
          
          <!-- View Details Button -->
          <div class="text-center mt-3">
            <v-btn 
              small 
              text 
              color="primary" 
              @click="showTechDebtDetails = !showTechDebtDetails"
            >
              <v-icon left small>{{ showTechDebtDetails ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              {{ showTechDebtDetails ? 'Hide Details' : 'View Details' }}
            </v-btn>
          </div>
          
          <!-- Expandable Details -->
          <v-expand-transition>
            <div v-if="showTechDebtDetails" class="mt-4">
              <!-- Debt Breakdown Bars -->
              <div class="mb-4">
                <div class="text-caption font-weight-medium mb-2">Debt Breakdown (hours)</div>
                <div v-for="(hours, category) in techDebt.perCategory" :key="category" class="mb-2">
                  <div class="d-flex justify-space-between">
                    <span class="text-caption text-capitalize">{{ category }}</span>
                    <span class="text-caption font-weight-bold">{{ hours.toFixed(1) }}h</span>
                  </div>
                  <v-progress-linear
                    :value="(hours / techDebt.total) * 100"
                    height="4"
                    :color="getCategoryColor(category)"
                    rounded
                  ></v-progress-linear>
                </div>
              </div>
              
              <!-- Top 3 Recommendations -->
              <div v-if="techDebt.recommendations.length">
                <div class="text-caption font-weight-medium mb-2">Top Recommendations</div>
                <v-list dense class="transparent">
                  <v-list-item 
                    v-for="rec in techDebt.recommendations.slice(0,3)" 
                    :key="rec.message"
                    class="px-0"
                  >
                    <v-list-item-icon>
                      <v-icon 
                        small 
                        :color="getRecommendationColor(rec.effort)"
                      >
                        {{ getRecommendationIcon(rec.effort) }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        {{ rec.message }}
                        <v-chip x-small :color="getEffortColor(rec.effort)" dark class="ml-1">
                          {{ rec.effort }}
                        </v-chip>
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <span class="text-caption">{{ rec.hours }}h</span>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </div>
            </div>
          </v-expand-transition>
        </v-col>
      </v-row>

    </v-card>
  </v-col>
</v-row>


          </v-card-text>
        </v-tab-item>

        <!-- Structure View - Responsive Tree -->
        <v-tab-item>
          <v-card-text class="pa-2 pa-sm-4">
            <v-row>
              <v-col cols="12">
                <div class="d-flex flex-wrap align-center mb-4 structure-header">
                  <div class="d-flex align-center mb-2 mb-sm-0">
                    <v-icon color="primary" class="mr-2">mdi-folder-tree</v-icon>
                    <span class="text-subtitle-1 font-weight-medium">Project Structure</span>
                  </div>
                  <v-spacer></v-spacer>
                  <div class="d-flex structure-actions">
                    <v-btn
                      small
                      text
                      color="primary"
                      @click="expandAll"
                      :disabled="!projectTree"
                      class="mr-1 action-btn"
                    >
                      <v-icon left small>mdi-arrow-expand-all</v-icon>
                      <span class="d-none d-sm-inline">Expand All</span>
                    </v-btn>
                    <v-btn
                      small
                      text
                      color="secondary"
                      @click="collapseAll"
                      :disabled="!projectTree"
                      class="action-btn"
                    >
                      <v-icon left small>mdi-arrow-collapse-all</v-icon>
                      <span class="d-none d-sm-inline">Collapse All</span>
                    </v-btn>
                  </div>
                </div>
                
                <v-card 
                  v-if="projectTree" 
                  outlined 
                  class="pa-2 pa-sm-3 structure-tree"
                  style="max-height: 500px; overflow-y: auto;"
                >
                  <v-treeview
                    v-model="selectedNodes"
                    :items="filteredProjectTree"
                    :open="openNodes"
                    :search="searchQuery"
                    :filter="treeFilter"
                    item-key="id"
                    item-text="name"
                    item-children="children"
                    hoverable
                    open-on-click
                    transition
                    class="responsive-treeview"
                    @update:open="onOpenUpdate"
                  >
                    <template v-slot:prepend="{ item, open }">
                      <v-icon v-if="item.type === 'folder'" :color="open ? 'primary' : 'grey darken-1'">
                        {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
                      </v-icon>
                      <v-icon v-else :color="getFileIconColor(item.extension)">
                        {{ getFileIcon(item.extension) }}
                      </v-icon>
                    </template>
                    
                    <template v-slot:label="{ item }">
                      <div class="d-flex flex-wrap align-center tree-label">
                        <span :class="{'search-highlight': isSearchMatch(item)}">{{ item.name }}</span>
                        <div class="tree-chips">
                          <v-chip
                            v-if="item.type === 'file' && item.lines !== undefined"
                            x-small
                            :color="getLineCountColor(item.lines)"
                            class="ml-2 lines-chip"
                            dark
                          >
                            {{ item.lines }} lines
                          </v-chip>
                          <v-chip
                            v-if="item.type === 'folder' && item.fileCount"
                            x-small
                            color="grey"
                            class="ml-2 files-chip"
                            outlined
                          >
                            {{ item.fileCount }} files
                          </v-chip>
                          <v-chip
                            v-if="item.type === 'file' && item.complexity"
                            x-small
                            :color="getComplexityColor(item.complexity)"
                            class="ml-2 complexity-chip"
                            dark
                          >
                            Cpx: {{ item.complexity }}
                          </v-chip>
                        </div>
                      </div>
                    </template>
                    
                    <template v-slot:append="{ item }">
                      <span v-if="item.type === 'file' && item.size" class="text-caption file-size">
                        {{ formatFileSize(item.size) }}
                      </span>
                    </template>
                  </v-treeview>
                </v-card>
                
                <v-alert
                  v-else
                  type="info"
                  outlined
                  class="mt-2"
                >
                  <v-icon left>mdi-information</v-icon>
                  Project structure will appear here after analysis
                </v-alert>
                
                <v-row v-if="projectTree" class="mt-4">
                  <v-col cols="6" sm="3" v-for="stat in structureStats" :key="stat.label">
                    <v-card elevation="1" class="text-center pa-2 structure-stat">
                      <div class="text-h6 font-weight-bold primary--text">
                        {{ stat.value }}
                      </div>
                      <div class="text-caption text--secondary">
                        {{ stat.label }}
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>

        <!-- Feature #1 - Charts Tab - Responsive -->
        <v-tab-item>
          <v-card-text class="pa-2 pa-sm-4">
            <v-row>
              <v-col cols="12" md="6">
                <v-card elevation="1" class="pa-3 pa-sm-4 chart-card">
                  <div class="text-subtitle-1 font-weight-medium mb-3 chart-title">
                    Lines by File Type
                  </div>
                  <div class="chart-container">
                    <canvas ref="pieChart"></canvas>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-card elevation="1" class="pa-3 pa-sm-4 chart-card">
                  <div class="text-subtitle-1 font-weight-medium mb-3 chart-title">
                    File Size Distribution
                  </div>
                  <div class="chart-container">
                    <canvas ref="barChart"></canvas>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-card elevation="1" class="pa-3 pa-sm-4 chart-card">
                  <div class="text-subtitle-1 font-weight-medium mb-3 chart-title">
                    Complexity Distribution
                  </div>
                  <div class="chart-container">
                    <canvas ref="complexityChart"></canvas>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-card elevation="1" class="pa-3 pa-sm-4 chart-card">
                  <div class="text-subtitle-1 font-weight-medium mb-3 chart-title">
                    Language Breakdown
                  </div>
                  <div class="chart-container">
                    <canvas ref="languageChart"></canvas>
                  </div>
                </v-card>
              </v-col>
            </v-row>
            
            <v-row class="mt-4">
              <v-col cols="12">
                <v-card elevation="1" class="pa-3 pa-sm-4 chart-card">
                  <div class="d-flex flex-wrap justify-space-between align-center mb-3">
                    <div class="text-subtitle-1 font-weight-medium">
                      Files Over Time (by size)
                    </div>
                    <v-btn small text @click="exportChartData" class="export-btn">
                      <v-icon left small>mdi-download</v-icon>
                      <span class="d-none d-sm-inline">Export Chart Data</span>
                    </v-btn>
                  </div>
                  <div class="chart-container line-chart">
                    <canvas ref="lineChart"></canvas>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>

<!-- Feature #2 - Complexity Tab - FIXED: Custom layout to prevent horizontal scroll -->
<!-- In the Complexity Tab - Feature #2 -->
<v-tab-item>
  <v-card-text class="pa-2 pa-sm-4">
    <!-- Stats Cards - Use roundedComplexityStats -->
    <v-row class="mb-4" dense>
      <v-col cols="6" md="3" v-for="stat in roundedComplexityStats" :key="stat.label">
        <v-card elevation="1" class="text-center pa-2 pa-sm-3 complexity-stat-card">
          <div class="text-h6 text-sm-h5 font-weight-bold" :class="stat.color">
            {{ stat.value }}
          </div>
          <div class="text-caption text--secondary">{{ stat.label }}</div>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Most Complex Files - Already rounded in display -->
    <v-card elevation="1" class="pa-3 pa-sm-4 mb-4">
      <div class="text-subtitle-1 font-weight-medium mb-3">
        Most Complex Files
      </div>
      <div class="complex-files-list">
        <div v-for="file in complexFiles.slice(0,10)" :key="file.name" class="complex-file-item mb-3 pa-2" style="background: rgba(0,0,0,0.02); border-radius: 4px;">
          <div class="d-flex flex-wrap align-center">
            <span class="file-name font-weight-medium" style="flex: 1 1 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
              {{ file.name }}
            </span>
            <div class="d-flex align-center ml-auto">
              <v-chip :color="getComplexityColor(file.complexity)" small dark class="mr-2">
                {{ file.complexity }}
              </v-chip>
              <div class="d-flex align-center" style="width: 80px;">
                <v-progress-linear
                  :value="(file.nesting / maxNesting) * 100"
                  height="6"
                  :color="getNestingColor(file.nesting)"
                  rounded
                  class="mr-1"
                  style="width: 40px;"
                ></v-progress-linear>
                <span class="text-caption">{{ file.nesting }}</span>
              </div>
            </div>
          </div>
          <div class="d-flex flex-wrap text-caption text--secondary mt-1">
            <span class="mr-3">Functions: {{ file.functions }}</span>
            <span>Lines: {{ file.lines }}</span>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Complexity by Language - Round avg complexity -->
    <v-card elevation="1" class="pa-3 pa-sm-4">
      <div class="text-subtitle-1 font-weight-medium mb-3">
        Complexity Analysis by Language
      </div>
      <div class="language-complexity-list">
        <div v-for="lang in languageComplexity" :key="lang.language" class="language-item mb-2 pa-2" style="background: rgba(0,0,0,0.02); border-radius: 4px;">
          <div class="d-flex flex-wrap align-center">
            <div class="d-flex align-center" style="min-width: 100px;">
              <v-icon small :color="getLanguageColor(lang.language)" class="mr-1">
                {{ getLanguageIcon(lang.language) }}
              </v-icon>
              <span class="language-name" style="max-width: 70px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ lang.language }}</span>
            </div>
            <div class="d-flex flex-wrap align-center ml-auto">
              <v-chip small :color="getComplexityColor(lang.avgComplexity)" class="mr-2">
                Avg: {{ lang.avgComplexity.toFixed(1) }}
              </v-chip>
              <span class="text-caption mr-2">Max: {{ lang.maxComplexity }}</span>
              <span class="text-caption d-none d-sm-inline">Func: {{ lang.functions }}</span>
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </v-card-text>
</v-tab-item>

        <!-- Feature #4 - Duplicates Tab - Responsive -->
        <v-tab-item>
          <v-card-text class="pa-2 pa-sm-4">
            <v-alert
              v-if="duplicates.length === 0"
              type="success"
              outlined
              class="mb-4"
            >
              <v-icon left>mdi-check-circle</v-icon>
              No duplicate code blocks found! Your codebase looks clean.
            </v-alert>

            <v-expansion-panels v-else v-model="openDuplicates" multiple>
              <v-expansion-panel
                v-for="(dup, index) in duplicates"
                :key="index"
                class="duplicate-panel"
              >
                <v-expansion-panel-header>
                  <div class="d-flex flex-wrap align-center duplicate-header">
                    <v-icon color="warning" class="mr-2">mdi-content-copy</v-icon>
                    <span class="font-weight-medium duplicate-original">{{ dup.original.split('/').pop() }}</span>
                    <div class="duplicate-chips">
                      <v-chip small color="warning" class="ml-2" dark>
                        {{ dup.similarity }} match
                      </v-chip>
                      <v-chip small color="grey" class="ml-2" outlined>
                        {{ dup.lines }} lines
                      </v-chip>
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="pa-3">
                    <div class="d-flex align-center mb-3">
                      <v-icon small color="error" class="mr-1">mdi-arrow-right</v-icon>
                      <span class="text-caption">Duplicated in:</span>
                    </div>
                    <v-list dense class="duplicate-files-list">
                      <v-list-item
                        v-for="(file, fileIndex) in dup.duplicates"
                        :key="fileIndex"
                      >
                        <v-list-item-icon>
                          <v-icon small>mdi-file</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="duplicate-file-name">{{ file }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn icon small @click="showDuplicateCode(dup)">
                            <v-icon small>mdi-eye</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                    
                    <div class="mt-3">
                      <v-btn
                        small
                        text
                        color="primary"
                        @click="showDuplicateCode(dup)"
                      >
                        <v-icon left small>mdi-code-tags</v-icon>
                        View Code
                      </v-btn>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-card v-if="duplicates.length > 0" class="mt-4 pa-3 pa-sm-4" elevation="1">
              <div class="d-flex align-center duplicate-summary">
                <v-icon color="info" class="mr-2">mdi-information</v-icon>
                <span class="text-caption">
                  Found {{ duplicates.length }} duplicate blocks across {{ duplicateFiles }} files.
                  Consider refactoring to improve maintainability.
                </span>
              </div>
            </v-card>
          </v-card-text>
        </v-tab-item>

        <!-- Feature #5 - Language Analysis Tab - Responsive -->
        <v-tab-item>
          <v-card-text class="pa-2 pa-sm-4">
            <v-row>
              <v-col cols="12">
                <v-card elevation="1" class="pa-3 pa-sm-4 mb-4">
                  <div class="text-subtitle-1 font-weight-medium mb-3">
                    Language Overview
                  </div>
                  <div class="responsive-table">
                    <v-simple-table class="language-stats-table">
                      <thead>
                        <tr>
                          <th>Language</th>
                          <th>Files</th>
                          <th>Lines</th>
                          <th class="d-none d-sm-table-cell">Functions</th>
                          <th class="d-none d-sm-table-cell">Classes</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="lang in languageStats" :key="lang.language">
                          <td>
                            <v-chip small :color="getLanguageColor(lang.language)" dark>
                              <v-icon left small>{{ getLanguageIcon(lang.language) }}</v-icon>
                              {{ lang.language }}
                            </v-chip>
                          </td>
                          <td>{{ lang.files }}</td>
                          <td>{{ lang.lines.toLocaleString() }}</td>
                          <td class="d-none d-sm-table-cell">{{ lang.functions }}</td>
                          <td class="d-none d-sm-table-cell">{{ lang.classes }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-card elevation="1" class="pa-3 pa-sm-4 mb-4 mb-md-0">
                  <div class="text-subtitle-1 font-weight-medium mb-3">
                    Top Imports
                  </div>
                  <div class="imports-list" v-for="(imports, lang) in topImports" :key="`group-${lang}`">
                    <v-subheader class="import-group-header">{{ lang }}</v-subheader>
                    <v-list dense class="import-items">
                      <v-list-item 
                        v-for="(imp, idx) in imports.slice(0,5)" 
                        :key="`${lang}-${idx}`"
                      >
                        <v-list-item-icon>
                          <v-icon small>mdi-import</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="import-name">{{ imp.name }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-chip x-small>{{ imp.count }}x</v-chip>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </div>
                </v-card>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-card elevation="1" class="pa-3 pa-sm-4">
                  <div class="text-subtitle-1 font-weight-medium mb-3">
                    TODO/FIXME Comments
                  </div>
                  <v-list dense class="todo-list" style="max-height: 400px; overflow-y: auto;">
                    <template v-if="todoComments.length > 0">
                      <v-list-item v-for="(todo, idx) in todoComments" :key="idx" class="todo-item">
                        <v-list-item-icon>
                          <v-icon small :color="todo.type === 'FIXME' ? 'error' : 'warning'">
                            {{ todo.type === 'FIXME' ? 'mdi-bug' : 'mdi-clock-alert' }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="todo-comment">{{ todo.comment }}</v-list-item-title>
                          <v-list-item-subtitle class="todo-location">
                            {{ todo.file.split('/').pop() }}:{{ todo.line }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                    <v-list-item v-else>
                      <v-list-item-content>
                        <v-list-item-title class="text-center text--secondary">
                          No TODO/FIXME comments found
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>

<!-- Feature #6 - Dependencies Tab - Responsive with Pagination -->
<v-tab-item>
  <v-card-text class="pa-2 pa-sm-4">
    <v-row class="mb-4">
      <v-col cols="6" md="3" v-for="stat in dependencyStats" :key="stat.label">
        <v-card elevation="1" class="text-center pa-2 pa-sm-3 dependency-stat-card">
          <div class="text-h6 text-sm-h5 font-weight-bold primary--text">
            {{ stat.value }}
          </div>
          <div class="text-caption text--secondary">{{ stat.label }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card elevation="1" class="pa-3 pa-sm-4 mb-4">
      <div class="d-flex justify-space-between align-center mb-3">
        <div class="text-subtitle-1 font-weight-medium">
          Dependency Graph
        </div>
        <div>
          <v-btn-toggle v-model="graphView" mandatory class="graph-toggle">
            <v-btn small value="tree">Tree</v-btn>
            <v-btn small value="list">List</v-btn>
            <v-btn small value="matrix">Matrix</v-btn>
          </v-btn-toggle>
        </div>
      </div>

      <!-- Tree View with Pagination -->
      <div v-if="graphView === 'tree'" class="dependency-tree-container">
        <div class="d-flex justify-space-between align-center mb-3">
          <div></div>
          <div v-if="dependencyTree.length > 0" class="d-flex align-center">
            <v-btn 
              icon 
              small 
              @click="prevTreePage" 
              :disabled="treePage === 1"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="mx-2 text-caption">
              Page {{ treePage }} of {{ treeTotalPages }}
            </span>
            <v-btn 
              icon 
              small 
              @click="nextTreePage" 
              :disabled="treePage === treeTotalPages"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
        
        <v-treeview
          :items="paginatedDependencyTree"
          item-key="name"
          item-text="name"
          item-children="dependencies"
          hoverable
          class="dependency-tree"
        >
          <template v-slot:prepend="{ item }">
            <v-icon :color="getDependencyIconColor(item)" small>
              {{ getDependencyIcon(item) }}
            </v-icon>
          </template>
          <template v-slot:label="{ item }">
            <div class="d-flex flex-wrap align-center">
              <span class="font-weight-medium dependency-name">{{ item.name.split('/').pop() }}</span>
              <v-chip
                v-if="item.dependents"
                x-small
                class="ml-2 dependents-chip"
                color="primary"
                outlined
              >
                used by {{ item.dependents.length }}
              </v-chip>
            </div>
          </template>
        </v-treeview>
        
        <!-- Bottom pagination for tree -->
        <div v-if="treeTotalPages > 1" class="d-flex justify-center mt-4">
          <v-pagination
            v-model="treePage"
            :length="treeTotalPages"
            total-visible="5"
            circle
            size="small"
          ></v-pagination>
        </div>
      </div>

      <!-- List View (already paginated by slice) -->
      <v-list v-else-if="graphView === 'list'" two-line class="dependency-list">
        <v-list-item-group>
          <v-list-item v-for="file in paginatedAllFiles" :key="file.name" class="dependency-list-item">
            <v-list-item-content>
              <v-list-item-title class="dependency-list-title">
                {{ file.name }}
                <v-chip
                  v-if="dependencyGraph[file.name]"
                  x-small
                  class="ml-2"
                  color="primary"
                >
                  {{ dependencyGraph[file.name].dependencies.length }} deps
                </v-chip>
              </v-list-item-title>
              <v-list-item-subtitle class="dependency-list-subtitle">
                <span v-if="dependencyGraph[file.name]">
                  Depends on: {{ dependencyGraph[file.name].dependencies.slice(0,3).join(', ') || 'none' }}
                  {{ dependencyGraph[file.name].dependencies.length > 3 ? '...' : '' }}
                </span>
                <span v-else>No dependencies</span>
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-menu offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" small>
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="showDependents(file.name)">
                    <v-list-item-title>Show Dependents</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="analyzeCircular(file.name)">
                    <v-list-item-title>Check Circular</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-list-item-action>
          </v-list-item>
        </v-list-item-group>
        
        <!-- Pagination for list view -->
        <div class="d-flex justify-center mt-4">
          <v-pagination
            v-model="filesListPage"
            :length="filesListTotalPages"
            total-visible="5"
            circle
            size="small"
          ></v-pagination>
        </div>
      </v-list>

      <!-- Matrix View - Responsive (already paginated) -->
      <div v-else class="matrix-container responsive-table">
        <v-simple-table class="dependency-matrix">
          <thead>
            <tr>
              <th class="matrix-corner"></th>
              <th v-for="file in paginatedMatrixFiles" :key="file.name" class="matrix-header">
                {{ file.name.split('/').pop() }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="file in paginatedMatrixFiles" :key="file.name">
              <td class="font-weight-medium matrix-row-label">{{ file.name }}</td>
              <td v-for="other in paginatedMatrixFiles" :key="other.name" class="text-center matrix-cell">
                <v-icon
                  v-if="hasDependency(file.name, other.name)"
                  small
                  color="success"
                >
                  mdi-check
                </v-icon>
                <span v-else class="text--disabled matrix-empty">-</span>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        
        <!-- Pagination for matrix view -->
        <div class="d-flex justify-center mt-4">
          <v-pagination
            v-model="matrixPage"
            :length="matrixTotalPages"
            total-visible="5"
            circle
            size="small"
          ></v-pagination>
        </div>
      </div>
    </v-card>

    <v-card elevation="1" class="pa-3 pa-sm-4">
      <div class="text-subtitle-1 font-weight-medium mb-3">
        Circular Dependencies
      </div>
      <v-alert
        v-if="circularDependencies.length === 0"
        type="success"
        outlined
      >
        No circular dependencies detected!
      </v-alert>
      <v-list v-else class="circular-list">
        <v-list-item v-for="(circle, idx) in paginatedCircularDeps" :key="idx" class="circular-item">
          <v-list-item-icon>
            <v-icon color="error">mdi-alert-circle</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="circular-path">
              {{ circle.join(' → ') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      
      <!-- Pagination for circular dependencies -->
      <div v-if="circularTotalPages > 1" class="d-flex justify-center mt-4">
        <v-pagination
          v-model="circularPage"
          :length="circularTotalPages"
          total-visible="5"
          circle
          size="small"
        ></v-pagination>
      </div>
    </v-card>
  </v-card-text>
</v-tab-item>
<!-- ===== ISSUES TAB CONTENT ===== -->
<v-tab-item>
  <v-card-text class="pa-2 pa-sm-4">
    
    <!-- Summary Cards -->
    <v-row class="mb-4">
      <v-col cols="6" md="4">
        <v-card elevation="1" class="text-center pa-3">
          <div class="text-h5 font-weight-bold error--text">
            {{ errorSummary && errorSummary.totalErrors ? errorSummary.totalErrors : 0 }}
          </div>
          <div class="text-caption">Errors</div>
        </v-card>
      </v-col>

      <v-col cols="6" md="4">
        <v-card elevation="1" class="text-center pa-3">
          <div class="text-h5 font-weight-bold warning--text">
            {{ errorSummary && errorSummary.totalWarnings ? errorSummary.totalWarnings : 0 }}
          </div>
          <div class="text-caption">Warnings</div>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card elevation="1" class="text-center pa-3">
          <div class="text-h5 font-weight-bold primary--text">
            {{
              (errorSummary && errorSummary.totalErrors ? errorSummary.totalErrors : 0) +
              (errorSummary && errorSummary.totalWarnings ? errorSummary.totalWarnings : 0)
            }}
          </div>
          <div class="text-caption">Total Issues</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Issues by Type with Pagination -->
    <v-card elevation="1" class="pa-3 pa-sm-4">
      <div class="d-flex justify-space-between align-center mb-3">
        <div class="text-subtitle-1 font-weight-medium">
          Issues by Type
        </div>
        <div v-if="issueTypesList.length > issuesPerPage" class="d-flex align-center">
          <v-btn 
            icon 
            small 
            @click="prevIssuePage" 
            :disabled="issuePage === 1"
          >
            <v-icon small>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="mx-2 text-caption">
            {{ issuePage }} / {{ issueTotalPages }}
          </span>
          <v-btn 
            icon 
            small 
            @click="nextIssuePage" 
            :disabled="issuePage === issueTotalPages"
          >
            <v-icon small>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <template v-if="errorSummary && errorSummary.byType">
        <v-expansion-panels v-model="openIssues" multiple>
          <v-expansion-panel
            v-for="(issue, key) in paginatedIssueTypes"
            :key="key"
            class="mb-2"
          >
            <v-expansion-panel-header>
              <div class="d-flex align-center">
                <v-icon
                  :color="issue.type === 'error' ? 'error' : 'warning'"
                  class="mr-2"
                >
                  {{ issue.type === 'error' ? 'mdi-alert-circle' : 'mdi-alert' }}
                </v-icon>

                <span class="font-weight-medium">
                  {{ issue.message }}
                </span>

                <v-chip
                  small
                  :color="issue.type === 'error' ? 'error' : 'warning'"
                  class="ml-3"
                  dark
                >
                  {{ issue.count }}
                </v-chip>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-list dense>
                <v-list-item
                  v-for="file in issue.files.slice(0, 5)"
                  :key="file"
                >
                  <v-list-item-icon>
                    <v-icon small>mdi-file</v-icon>
                  </v-list-item-icon>

                  <v-list-item-content>
                    <v-list-item-title>
                      {{ file.split('/').pop() }}
                    </v-list-item-title>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-chip
                      x-small
                      :color="issue.severity === 'high' ? 'error' : 'grey'"
                    >
                      {{ issue.severity }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>

                <v-list-item v-if="issue.files && issue.files.length > 5">
                  <v-list-item-content>
                    <span class="text-caption">
                      ... and {{ issue.files.length - 5 }} more
                    </span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        
        <!-- Bottom pagination for many pages -->
        <div v-if="issueTotalPages > 1" class="d-flex justify-center mt-4">
          <v-pagination
            v-model="issuePage"
            :length="issueTotalPages"
            total-visible="5"
            circle
            size="small"
          ></v-pagination>
        </div>
      </template>

      <v-alert
        v-if="!errorSummary || !errorSummary.byType || Object.keys(errorSummary.byType).length === 0"
        type="success"
        outlined
        class="mt-3"
      >
        <v-icon left>mdi-check-circle</v-icon>
        No issues found! Your code looks clean.
      </v-alert>
    </v-card>

    <!-- Files with Issues with Pagination -->
    <v-card elevation="1" class="pa-3 pa-sm-4 mt-4">
      <div class="d-flex justify-space-between align-center mb-3">
        <div class="text-subtitle-1 font-weight-medium">
          Files with Issues
        </div>
        <div v-if="filesWithIssues.length > filesPerPage" class="d-flex align-center">
          <v-btn 
            icon 
            small 
            @click="prevFilesPage" 
            :disabled="filesPage === 1"
          >
            <v-icon small>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="mx-2 text-caption">
            {{ filesPage }} / {{ filesTotalPages }}
          </span>
          <v-btn 
            icon 
            small 
            @click="nextFilesPage" 
            :disabled="filesPage === filesTotalPages"
          >
            <v-icon small>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <v-list two-line>
        <v-list-item
          v-for="file in paginatedFilesWithIssues"
          :key="file.name"
        >
          <v-list-item-content>
            <v-list-item-title>
              {{ file.name }}
            </v-list-item-title>

            <v-list-item-subtitle>
              <span
                v-if="file.errors && file.errors.length"
                class="error--text mr-2"
              >
                {{ file.errors.length }} errors
              </span>

              <span
                v-if="file.warnings && file.warnings.length"
                class="warning--text"
              >
                {{ file.warnings.length }} warnings
              </span>
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <v-btn icon small @click="showFileIssues(file)">
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>

        <v-list-item v-if="!filesWithIssues.length">
          <v-list-item-content>
            <v-list-item-title class="text-center text--secondary">
              No files with issues
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      
      <!-- Bottom pagination for files -->
      <div v-if="filesTotalPages > 1" class="d-flex justify-center mt-4">
        <v-pagination
          v-model="filesPage"
          :length="filesTotalPages"
          total-visible="5"
          circle
          size="small"
        ></v-pagination>
      </div>
    </v-card>

  </v-card-text>
</v-tab-item>

        
      </v-tabs-items>
      

<!-- Enhanced Actions - Fully Responsive -->
<v-card-actions class="justify-center pa-4 flex-wrap action-buttons">
  <v-btn @click="exportResults" color="primary" outlined class="ma-1 action-btn" small>
    <v-icon left small>mdi-download</v-icon>
    <span class="d-none d-sm-inline">Export JSON</span>
  </v-btn>
  <v-btn @click="exportCSV" color="secondary" outlined class="ma-1 action-btn" small>
    <v-icon left small>mdi-file-excel</v-icon>
    <span class="d-none d-sm-inline">Export CSV</span>
  </v-btn>
  <v-btn @click="exportStructure" color="info" outlined class="ma-1 action-btn" small>
    <v-icon left small>mdi-file-tree</v-icon>
    <span class="d-none d-sm-inline">Export Structure</span>
  </v-btn>
  <!-- NEW: Health Report Button -->
  <v-btn 
    @click="exportHealthReport" 
    :color="healthScoreColor"
    outlined 
    class="ma-1 action-btn" 
    small
  >
    <v-icon left small>{{ healthScoreIcon }}</v-icon>
    <span class="d-none d-sm-inline">Health Report</span>
  </v-btn>
  <v-btn @click="generateHTMLReport" color="success" outlined class="ma-1 action-btn" small>
    <v-icon left small>mdi-file-document</v-icon>
    <span class="d-none d-sm-inline">HTML Report</span>
  </v-btn>
  <v-btn @click="startNewAnalysis" color="warning" outlined class="ma-1 action-btn" small>
    <v-icon left small>mdi-refresh</v-icon>
    <span class="d-none d-sm-inline">New Analysis</span>
  </v-btn>
</v-card-actions>
    </v-card>
  </div>
</v-expand-transition>

        <!-- Snackbars -->
        <v-snackbar v-model="showError" :timeout="5000" color="error" top>
          {{ errorMessage }}
          <template v-slot:action="{ attrs }">
            <v-btn icon v-bind="attrs" @click="showError = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-snackbar>

        <v-snackbar v-model="showWarning" :timeout="5000" color="warning" top>
          <v-icon left>mdi-alert</v-icon>
          {{ warningMessage }}
          <template v-slot:action="{ attrs }">
            <v-btn icon v-bind="attrs" @click="showWarning = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-snackbar>

        <v-snackbar v-model="showSuccess" :timeout="5000" color="success" top>
          {{ successMessage }}
          <template v-slot:action="{ attrs }">
            <v-btn icon v-bind="attrs" @click="showSuccess = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-snackbar>

        <!-- Code Viewer Dialog -->
        <v-dialog v-model="showCodeDialog" max-width="800px">
          <v-card>
            <v-card-title class="primary white--text">
              <v-icon left dark>mdi-code-tags</v-icon>
              Duplicate Code
              <v-spacer></v-spacer>
              <v-btn icon dark @click="showCodeDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pa-4">
              <pre class="code-preview"><code>{{ selectedCode }}</code></pre>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Add this dialog after your other dialogs (around line 1400) -->
<!-- Instagram Share Preview Dialog -->
<v-dialog v-model="shareDialog" max-width="600px">
  <v-card>
    <v-card-title class="primary white--text">
      <v-icon left dark>mdi-instagram</v-icon>
      Instagram Ready Screenshot
      <v-spacer></v-spacer>
      <v-btn icon dark @click="shareDialog = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    
    <v-card-text class="text-center pa-4">
      <v-img
        v-if="shareImageUrl"
        :src="shareImageUrl"
        max-height="500"
        contain
        class="mb-4"
      ></v-img>
      
      <div class="text-body-2 mb-3">
        <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
        Perfect for Instagram! 1080x1080 optimized.
      </div>
      
      <div class="d-flex justify-center">
        <v-btn
          color="primary"
          :href="shareImageUrl"
          download="code-health-instagram.png"
          class="mx-2"
        >
          <v-icon left>mdi-download</v-icon>
          Download Image
        </v-btn>
        
        <v-btn
          color="secondary"
          @click="copyStats"
          class="mx-2"
        >
          <v-icon left>mdi-content-copy</v-icon>
          Copy Caption
        </v-btn>
      </div>
      
      <v-divider class="my-4"></v-divider>
      
      <div class="text-caption text--secondary">
        <strong>Suggested Caption:</strong><br>
        📊 Code Health Score: {{ overallHealthScore }}/100<br>
        📁 {{ totalFilesProcessed }} files • 📝 {{ totalLines.toLocaleString() }} lines<br>
        ⚡ Avg Complexity: {{ (complexityMetrics.avgComplexity || 0).toFixed(1) }}<br>
        🔄 {{ duplicates.length }} duplicates • ⏰ {{ todoComments.length }} TODOs<br><br>
        #CodeHealth #DevLife #CodeQuality #Programming
      </div>
    </v-card-text>
    
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text @click="shareDialog = false">
        Close
      </v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>

  <!-- Issues Dialog -->
<v-dialog v-model="showIssuesDialog" max-width="800px" v-if="selectedFileForIssues">
  <v-card>
    <v-card-title :class="formattedIssues.errors.length > 0 ? 'error white--text' : 'warning white--text'">
      <v-icon left dark>
        {{ formattedIssues.errors.length > 0 ? 'mdi-alert-circle' : 'mdi-alert' }}
      </v-icon>
      Issues in {{ selectedFileForIssues.name.split('/').pop() }}
      <v-spacer></v-spacer>
      <v-btn icon dark @click="showIssuesDialog = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    
    <v-card-text class="pa-4">
      <v-row>
        <!-- Summary Cards -->
        <v-col cols="6">
          <v-card elevation="1" class="text-center pa-3">
            <div class="text-h5 font-weight-bold error--text">
              {{ formattedIssues.errors.length }}
            </div>
            <div class="text-caption">Errors</div>
          </v-card>
        </v-col>
        <v-col cols="6">
          <v-card elevation="1" class="text-center pa-3">
            <div class="text-h5 font-weight-bold warning--text">
              {{ formattedIssues.warnings.length }}
            </div>
            <div class="text-caption">Warnings</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Errors Section -->
      <v-expansion-panels v-if="formattedIssues.errors.length > 0" class="mt-4" multiple>
        <v-expansion-panel>
          <v-expansion-panel-header class="error lighten-4">
            <div class="d-flex align-center">
              <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
              <span class="font-weight-medium error--text">Errors ({{ formattedIssues.errors.length }})</span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-list dense>
              <v-list-item
                v-for="(error, index) in formattedIssues.errors"
                :key="'error-' + index"
                class="border-bottom"
              >
                <v-list-item-icon>
                  <v-icon small color="error">mdi-close-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    Line {{ error.line }}: {{ error.message }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip x-small :color="getSeverityColor(error.severity)" dark>
                      {{ error.severity }} severity
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Warnings Section -->
      <v-expansion-panels v-if="formattedIssues.warnings.length > 0" class="mt-4" multiple>
        <v-expansion-panel>
          <v-expansion-panel-header class="warning lighten-4">
            <div class="d-flex align-center">
              <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
              <span class="font-weight-medium warning--text">Warnings ({{ formattedIssues.warnings.length }})</span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-list dense>
              <v-list-item
                v-for="(warning, index) in formattedIssues.warnings"
                :key="'warning-' + index"
                class="border-bottom"
              >
                <v-list-item-icon>
                  <v-icon small color="warning">mdi-alert</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    Line {{ warning.line }}: {{ warning.message }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip x-small :color="getSeverityColor(warning.severity)" dark>
                      {{ warning.severity }} severity
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- No Issues Message -->
      <v-alert
        v-if="formattedIssues.errors.length === 0 && formattedIssues.warnings.length === 0"
        type="success"
        outlined
        class="mt-4"
      >
        <v-icon left>mdi-check-circle</v-icon>
        No issues found in this file!
      </v-alert>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        text
        @click="showIssuesDialog = false"
      >
        Close
      </v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>

      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import JSZip from 'jszip';
import { createCodeAnalyzerWorker } from '@/utils/worker-utils';
import Chart from 'chart.js/auto';
import html2canvas from 'html2canvas';

import { sanitizeFilename, sanitizeCode, sanitizeForDisplay, escapeHtml, SECURITY_CONFIG, isValidFileType, isSuspiciousFilename } from '@/utils/sanitize';


// Constants
const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB (down from 1GB)
const MAX_FOLDER_SIZE = 500 * 1024 * 1024; // 500MB - NOW WE'LL USE IT
// const MAX_FILES_IN_ZIP = 10000; // ADD THIS
// const MAX_COMPRESSION_RATIO = 100; // ADD THIS
// const MAX_EXTRACTED_SIZE = 500 * 1024 * 1024; // ADD THIS
const WARNING_SIZE = 100 * 1024 * 1024; // 100MB warning threshold
const LARGE_SIZE = 500 * 1024 * 1024; // 500MB large file threshold

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const context = this; // Capture the correct this context
    const later = () => {
      clearTimeout(timeout);
      func.apply(context, args); // Use apply to preserve context
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default {
  name: 'CodeAnalyzer',
  
  data() {
    return {
      // Original data properties
      selectedFile: null,
      selectedFolder: null,
      isProcessing: false,
      isDragging: false,
      progress: 0,
      currentFile: '',
      processedFiles: 0,
      totalFiles: 0,
      processingTime: 0,
      startTime: null,
      selectedTypes: [],
      countEmptyLines: false,
      ignoreComments: true,
      analysisComplete: false,
      totalLines: 0,
      totalFilesProcessed: 0,
      fileTypeSummary: [],
      largestFiles: [],
      allFiles: [],
      projectTree: null,
      openNodes: [],
      selectedNodes: [],
      activeTab: 0,
      showError: false,
      errorMessage: '',
      showSuccess: false,
      successMessage: '',
      showWarning: false,
      warningMessage: '',
      worker: null,
      
      // Feature #1 - Charts
      pieChart: null,
      barChart: null,
      complexityChart: null,
      languageChart: null,
      lineChart: null,
      
      // Feature #2 - Complexity
      complexityMetrics: {
        avgComplexity: 0,
        maxComplexity: 0,
        totalFunctions: 0,
        totalClasses: 0
      },
      complexFiles: [],
      languageComplexity: [],
      
      // Feature #3 - Search
      searchQuery: '',
      
      // Feature #4 - Duplicates
      duplicates: [],
      duplicateFiles: 0,
      openDuplicates: [],
      showCodeDialog: false,
      selectedCode: '',
      
      // Feature #5 - Language analysis
      languageStats: [],
      topImports: {},
      todoComments: [],

      // New search UI properties
      showSearchResults: false,
      
      // Infinite scroll properties
      visibleResultCount: 20, // Number of results to show initially
      resultsPerScroll: 10,    // Number to load each time
      isLoadingMore: false,
      intersectionObserver: null,
      searchPage: 1,
      searchPageSize: 10,
      
      // Enhanced search results with metadata
      enhancedSearchResults: [],
      
      // Feature #6 - Dependencies
      dependencyGraph: {},
      dependencyTree: [],
      circularDependencies: [],
      graphView: 'tree',
      errorSummary: null,
      openIssues: [],
      // Add these for the dialog
      showIssuesDialog: false,
      selectedFileForIssues: null,
      showTechDebtDetails: false,

      // Pagination for Issues tab
      issuePage: 1,
      issuesPerPage: 10,
      filesPage: 1,
      filesPerPage: 10,
      
      // Pagination for Dependencies tab
      treePage: 1,
      treesPerPage: 5,
      filesListPage: 1,
      filesListPerPage: 10,
      matrixPage: 1,
      matrixPerPage: 5,
      circularPage: 1,
      circularPerPage: 5,

      // Performance options - default to thorough
      thoroughAnalysis: true, // Master switch - ON by default
      analyzeDocumentation: true, // Will be auto-managed
      analyzeDependencies: true, // Will be auto-managed

            // ===== NEW: Debounced Search =====
      searchResults: [],
      isSearching: false,
      
      // ===== NEW: Progress Persistence =====
      autoSaveId: null,
      lastSaved: null,
      
      // ===== NEW: Code Churn Analysis =====
      codeChurn: {
        highChurnFiles: [],
        churnByType: {},
        recentChanges: 0,
        totalChanges: 0,
        stabilityScore: 100
      },
      fileHistory: new Map(),
      
      // ===== NEW: Tech Debt Estimation =====
      techDebt: {
        total: 0,
        perCategory: {
          complexity: 0,
          duplication: 0,
          documentation: 0,
          dependencies: 0,
          codeSmells: 0
        },
        recommendations: [],
        debtScore: 100,
        estimatedHours: 0
      },
      showHealthDetails: false,
      
      // Table headers
      typeHeaders: [
        { text: 'File Type', value: 'type', align: 'left' },
        { text: 'Files', value: 'count', align: 'center' },
        { text: 'Lines', value: 'lines', align: 'center' },
        { text: 'Percentage', value: 'percentage', align: 'center', sortable: false }
      ],
      // Add to your data() return object:
      shareMenu: false,
      isCapturingScreenshot: false,
      sharePlatforms: [
        { id: 'twitter', icon: 'mdi-twitter', color: '#1DA1F2', label: 'Twitter' },
        { id: 'reddit', icon: 'mdi-reddit', color: '#FF4500', label: 'Reddit' },
        { id: 'linkedin', icon: 'mdi-linkedin', color: '#0077B5', label: 'LinkedIn' },
        { id: 'instagram', icon: 'mdi-instagram', color: '#E4405F', label: 'Instagram' },
        { id: 'copy', icon: 'mdi-content-copy', color: '#757575', label: 'Copy Stats' }
      ],
      shareDialog: false,
      shareImageUrl: null,
      // Complexity headers
      complexityHeaders: [
        { text: 'File', value: 'name', align: 'left' },
        { text: 'Complexity', value: 'complexity', align: 'center' },
        { text: 'Nesting Depth', value: 'nesting', align: 'center' },
        { text: 'Functions', value: 'functions', align: 'center' },
        { text: 'Lines', value: 'lines', align: 'center' }
      ],
      
      // File types
      fileTypes: [
        { label: "Python (.py)", value: ".py" },
        { label: "JavaScript (.js)", value: ".js" },
        { label: "TypeScript (.ts)", value: ".ts" },
        { label: "HTML (.html)", value: ".html" },
        { label: "CSS (.css)", value: ".css" },
        { label: "SCSS (.scss)", value: ".scss" },
        { label: "Vue (.vue)", value: ".vue" },
        { label: "Java (.java)", value: ".java" },
        { label: "C/C++ (.c/.cpp)", value: ".c" },
        { label: "C# (.cs)", value: ".cs" },
        { label: "Go (.go)", value: ".go" },
        { label: "PHP (.php)", value: ".php" },
        { label: "Ruby (.rb)", value: ".rb" },
        { label: "JSON (.json)", value: ".json" },
        { label: "YAML (.yaml)", value: ".yaml" },
        { label: "XML (.xml)", value: ".xml" },
        { label: "Markdown (.md)", value: ".md" },
        { label: "Text (.txt)", value: ".txt" },
        { label: "Shell (.sh)", value: ".sh" },
        { label: "Dockerfile", value: "Dockerfile" },
        { label: "Makefile", value: "Makefile" }
      ],
    };
  },
  
 computed: {
// Added this computed property for tooltip text
  // Paginated search results
  paginatedSearchResults() {
    const start = (this.searchPage - 1) * this.searchPageSize;
    const end = start + this.searchPageSize;
    return this.enhancedSearchResults.slice(start, end);
  },
    // Get currently visible results (for infinite scroll)
  visibleSearchResults() {
    return this.enhancedSearchResults.slice(0, this.visibleResultCount);
  },
  
  // Check if there are more results to load
  hasMoreResults() {
    return this.visibleResultCount < this.enhancedSearchResults.length;
  },
  searchTotalPages() {
    return Math.ceil(this.enhancedSearchResults.length / this.searchPageSize) || 1;
  },
thoroughAnalysisTooltip() {
  if (this.thoroughAnalysis) {
    return 'Thorough mode: Analyzes documentation quality and builds dependency graph. Best for deep code review.';
  } else {
    return 'Fast mode: Quick analysis without documentation quality or dependency graph. Best for quick overview.';
  }
},

overallHealthScore() {
  const weights = {
    codeQuality: 0.35,
    uniqueness: 0.10,
    documentation: 0.10,
    maintainability: 0.25,
    complexity: 0.20
  };

  const metrics = this.healthMetrics;

  // Smooth complexity formula
  const avg = parseFloat(this.complexityMetrics.avgComplexity) || 0;
  const complexityScore = 100 / (1 + avg / 10);

  let score = 0;

  metrics.forEach(metric => {
    const value = metric.value;

    switch (metric.name) {
      case 'Code Quality':
        score += value * weights.codeQuality;
        break;
      case 'Uniqueness':
        score += value * weights.uniqueness;
        break;
      case 'Documentation':
        score += value * weights.documentation;
        break;
      case 'Maintainability':
        score += value * weights.maintainability;
        break;
    }
  });

  score += complexityScore * weights.complexity;

  return Math.round(Math.min(100, score));
},

healthScoreColor() {
  const score = this.overallHealthScore;
  if (score >= 80) return 'success';
  if (score >= 60) return 'info';
  if (score >= 40) return 'warning';
  return 'error';
},

healthScoreIcon() {
  const score = this.overallHealthScore;
  if (score >= 80) return 'mdi-star';
  if (score >= 60) return 'mdi-thumb-up';
  if (score >= 40) return 'mdi-alert';
  return 'mdi-alert-circle';
},

shareableHealthData() {
  return {
    overallScore: this.overallHealthScore,
    timestamp: new Date().toISOString(),
    project: {
      totalFiles: this.totalFilesProcessed,
      totalLines: this.totalLines,
      avgComplexity: parseFloat(this.complexityMetrics.avgComplexity).toFixed(1)
    },
    metrics: this.healthMetrics,
    duplicates: this.duplicates.length,
    todoComments: this.todoComments.length,
    circularDependencies: this.circularDependencies.length
  };
},
    
  // Issues pagination
  issueTypesList() {
    if (!this.errorSummary || !this.errorSummary.byType) return [];
    return Object.values(this.errorSummary.byType);
  },
  
  paginatedIssueTypes() {
    const start = (this.issuePage - 1) * this.issuesPerPage;
    const end = start + this.issuesPerPage;
    return this.issueTypesList.slice(start, end);
  },
  
  issueTotalPages() {
    return Math.ceil(this.issueTypesList.length / this.issuesPerPage) || 1;
  },
  
  paginatedFilesWithIssues() {
    const start = (this.filesPage - 1) * this.filesPerPage;
    const end = start + this.filesPerPage;
    return this.filesWithIssues.slice(start, end);
  },
  
  filesTotalPages() {
    return Math.ceil(this.filesWithIssues.length / this.filesPerPage) || 1;
  },
  
  // Dependencies pagination
  paginatedDependencyTree() {
    const start = (this.treePage - 1) * this.treesPerPage;
    const end = start + this.treesPerPage;
    return (this.dependencyTree || []).slice(start, end);
  },
  
  treeTotalPages() {
    return Math.ceil((this.dependencyTree || []).length / this.treesPerPage) || 1;
  },
  
  paginatedAllFiles() {
    const start = (this.filesListPage - 1) * this.filesListPerPage;
    const end = start + this.filesListPerPage;
    return (this.allFiles || []).slice(start, end);
  },
  
  filesListTotalPages() {
    return Math.ceil((this.allFiles || []).length / this.filesListPerPage) || 1;
  },
  
  paginatedMatrixFiles() {
    const start = (this.matrixPage - 1) * this.matrixPerPage;
    const end = start + this.matrixPerPage;
    return (this.allFiles || []).slice(start, end);
  },
  
  matrixTotalPages() {
    return Math.ceil((this.allFiles || []).length / this.matrixPerPage) || 1;
  },
  
  paginatedCircularDeps() {
    const start = (this.circularPage - 1) * this.circularPerPage;
    const end = start + this.circularPerPage;
    return (this.circularDependencies || []).slice(start, end);
  },
  
  circularTotalPages() {
    return Math.ceil((this.circularDependencies || []).length / this.circularPerPage) || 1;
  },
  hasSelectedFile() {
    return this.selectedFile || (this.selectedFolder && this.selectedFolder.length > 0);
  },
    filesWithIssues() {
    return this.allFiles.filter(f => f.errors?.length > 0 || f.warnings?.length > 0);
  },
  
  // Add this computed property for formatted issues
  formattedIssues() {
    if (!this.selectedFileForIssues) return { errors: [], warnings: [] };
    return {
      errors: this.selectedFileForIssues.errors || [],
      warnings: this.selectedFileForIssues.warnings || []
    };
  },
    roundedComplexityStats() {
    const avgComplexity = parseFloat(this.complexityMetrics.avgComplexity) || 0;
    const maxComplexity = parseInt(this.complexityMetrics.maxComplexity) || 0;
    const totalFunctions = parseInt(this.complexityMetrics.totalFunctions) || 0;
    const totalClasses = parseInt(this.complexityMetrics.totalClasses) || 0;
    
    return [
      { label: 'Average Complexity', value: avgComplexity.toFixed(1), color: 'primary--text' },
      { label: 'Max Complexity', value: maxComplexity, color: 'error--text' },
      { label: 'Total Functions', value: totalFunctions.toLocaleString(), color: 'success--text' },
      { label: 'Total Classes', value: totalClasses.toLocaleString(), color: 'info--text' }
    ];
  },
    selectedFileSize() {
      if (this.selectedFile?.size) {
        return this.selectedFile.size;
      } else if (this.selectedFolder?.length) {
        return this.selectedFolder.reduce((sum, file) => sum + (file.size || 0), 0);
      }
      return 0;
    },
  
  statistics() {
    const avgLines = this.totalFilesProcessed > 0 
      ? Math.round(this.totalLines / this.totalFilesProcessed) 
      : 0;
    
    const maxLines = this.largestFiles.length > 0 
      ? this.largestFiles[0].lines 
      : 0;
    
    const minLines = this.allFiles.length > 0 
      ? Math.min(...this.allFiles.map(f => f.lines)) 
      : 0;
    
    const totalSize = this.allFiles.reduce((sum, file) => sum + (file.size || 0), 0);
    
    return [
      { title: 'Avg Lines/File', value: avgLines.toLocaleString() },
      { title: 'Largest File', value: maxLines.toLocaleString() },
      { title: 'Smallest File', value: minLines.toLocaleString() },
      { title: 'Total Size', value: this.formatFileSize(totalSize) }
    ];
  },
  
  sizeRanges() {
    const ranges = [
      { label: 'Tiny (< 10 lines)', max: 10, color: 'blue', count: 0 },
      { label: 'Small (10-100)', max: 100, color: 'green', count: 0 },
      { label: 'Medium (100-500)', max: 500, color: 'yellow', count: 0 },
      { label: 'Large (500-1000)', max: 1000, color: 'orange', count: 0 },
      { label: 'Huge (> 1000)', max: Infinity, color: 'red', count: 0 }
    ];
    
    this.allFiles.forEach(file => {
      for (const range of ranges) {
        if (file.lines < range.max) {
          range.count++;
          break;
        }
      }
    });
    
    return ranges;
  },
  
  structureStats() {
    if (!this.projectTree) return [];
    const folderCount = this.countFolders(this.projectTree);
    const fileCount = this.totalFilesProcessed;
    const depth = this.getMaxDepth(this.projectTree);
    return [
      { label: 'Folders', value: folderCount },
      { label: 'Files', value: fileCount },
      { label: 'Max Depth', value: depth },
      { label: 'Root Items', value: this.projectTree.length }
    ];
  },
  
  // Feature #1 - Health metrics
healthMetrics() {
  // Use the actual docQuality data from worker
  let totalDocElements = 0;
  
  this.allFiles.forEach(file => {
    if (file.docQuality) {
      totalDocElements += file.docQuality.totalDocs;
    }
  });
  
  // Calculate documentation score based on actual documentation
  const documentationScore = this.totalFilesProcessed > 0 
    ? Math.min(100, Math.round((totalDocElements / this.totalFilesProcessed) * 10)) 
    : 0;
  
  const avgComplexity = parseFloat(this.complexityMetrics.avgComplexity) || 0;
  const complexityScore = avgComplexity 
    ? Math.max(0, 100 - avgComplexity * 2) 
    : 100;
  
  const duplicateScore = this.duplicates.length > 0 
    ? Math.max(0, 100 - this.duplicates.length * 5) 
    : 100;
  
  return [
    { name: 'Code Quality', value: Math.min(100, Math.round(complexityScore)), color: 'success' },
    { name: 'Uniqueness', value: Math.min(100, Math.round(duplicateScore)), color: 'info' },
    { name: 'Documentation', value: documentationScore, color: 'warning' },
    { name: 'Maintainability', value: Math.min(100, Math.round((complexityScore + duplicateScore) / 2)), color: 'primary' }
  ];
},
  
  // Feature #2 - Complexity stats
  complexityStats() {
    const avgComplexity = parseFloat(this.complexityMetrics.avgComplexity) || 0;
    const maxComplexity = parseInt(this.complexityMetrics.maxComplexity) || 0;
    const totalFunctions = parseInt(this.complexityMetrics.totalFunctions) || 0;
    const totalClasses = parseInt(this.complexityMetrics.totalClasses) || 0;
    
    return [
      { label: 'Average Complexity', value: avgComplexity.toFixed(1), color: 'primary--text' },
      { label: 'Max Complexity', value: maxComplexity, color: 'error--text' },
      { label: 'Total Functions', value: totalFunctions.toLocaleString(), color: 'success--text' },
      { label: 'Total Classes', value: totalClasses.toLocaleString(), color: 'info--text' }
    ];
  },
  
  maxNesting() {
    return Math.max(...this.complexFiles.map(f => f.nesting || 0), 10);
  },
  
  // Feature #3 - Filtered files for search
  filteredFiles() {
    return this.searchResults.length ? this.searchResults : this.allFiles;
  },
  
  filteredProjectTree() {
    if (!this.searchQuery || !this.projectTree) return this.projectTree;
    return this.filterTree(this.projectTree);
  },
  
  // Feature #6 - Dependency stats
  dependencyStats() {
    const totalDeps = Object.values(this.dependencyGraph).reduce(
      (sum, file) => sum + (file.dependencies?.length || 0), 0
    );
    const avgDeps = this.allFiles.length > 0 
      ? (totalDeps / this.allFiles.length).toFixed(1) 
      : 0;
    
    return [
      { label: 'Total Dependencies', value: totalDeps },
      { label: 'Avg per File', value: avgDeps },
      { label: 'Files with Dependencies', value: Object.keys(this.dependencyGraph).length },
      { label: 'Circular Dependencies', value: this.circularDependencies.length }
    ];
  },
  shareableText() {
  const healthData = this.shareableHealthData;
  const scoreEmoji = this.overallHealthScore >= 80 ? '🟢' : 
                     this.overallHealthScore >= 60 ? '🔵' : 
                     this.overallHealthScore >= 40 ? '🟠' : '🔴';
  
  return `${scoreEmoji} Code Health Score: ${healthData.overallScore}/100\n` +
    `📁 Files: ${healthData.project.totalFiles}\n` +
    `📝 Lines: ${healthData.project.totalLines.toLocaleString()}\n` +
    `⚡ Avg Complexity: ${healthData.project.avgComplexity}\n` +
    `🔄 Duplicates: ${healthData.duplicates}\n` +
    `⏰ TODOs: ${healthData.todoComments}\n\n` +
    `📊 Metrics:\n` +
    healthData.metrics.map(m => `  • ${m.name}: ${m.value}%`).join('\n') + '\n\n' +
    `🔗 Analyze your code at: ${window.location.origin}`;
},

shareableUrl() {
  return window.location.href;
}
},
  
watch: {
    thoroughAnalysis(val) {
    if (val) {
      this.analyzeDocumentation = true;
      this.analyzeDependencies = true;
    } else {
      this.analyzeDocumentation = false;
      this.analyzeDependencies = false;
    }

  },
  // Optional: Prevent manual changes when thorough is off
  // (these will be enforced by disabled state in UI, but this is a backup)
  analyzeDocumentation(val) {
    if (!this.thoroughAnalysis && val) {
      this.analyzeDocumentation = false;
    }
        if(!this.analyzeDocumentation && !this.analyzeDependencies) {
      this.thoroughAnalysis = false;
    } 
  },
  
  analyzeDependencies(val) {
    if (!this.thoroughAnalysis && val) {
      this.analyzeDependencies = false;
    }
    if(!this.analyzeDocumentation && !this.analyzeDependencies) {
      this.thoroughAnalysis = false;
    } 
  },
  analysisComplete(val) {
    if (val) {
      // Wait for DOM to update, then initialize charts
      this.$nextTick(() => {
        // Small delay to ensure DOM is fully rendered
        setTimeout(() => {
          this.initializeCharts();
        }, 100);
      });
    }
  },
  
  activeTab(val) {
    // Re-initialize charts when switching to charts tab
    if (val === 4 && this.analysisComplete) { // 4 is the index of Charts tab
      this.$nextTick(() => {
        setTimeout(() => {
          this.initializeCharts();
        }, 100);
      });
    }
  },
  
  searchQuery(newQuery) {
    this.debouncedSearch(newQuery);
  },
},

     // Lifecycle
 beforeDestroy() {
            // Stop auto-save
      this.stopAutoSave();
      
      // Remove beforeunload handler
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
      
      // Save final progress
      if (this.hasSelectedFile || this.selectedTypes.length) {
        this.saveProgress(false);
      }

        // Clean up intersection observer
  if (this.intersectionObserver) {
    this.intersectionObserver.disconnect();
  }
  
      this.cleanupWorker();
      this.cleanupMemory();
      this.destroyCharts();
    },

mounted() {
  this.showWarningMsg('⚠️ Security Notice: Only analyze code from sources you trust');

  this.$nextTick(() => {
    console.log('3. nextTick executed');
    
    setTimeout(() => {
      this.loadProgress(false);
    }, 100);
    
    setTimeout(() => {
      console.log('6. setTimeout executing startAutoSave');
      this.startAutoSave();
      console.log('7. startAutoSave completed');
    }, 200);
  });
  
  window.addEventListener('beforeunload', this.handleBeforeUnload);
  console.log('8. beforeunload handler added');
},
  
  methods: {
      getFileName(fullPath) {
    if (!fullPath) return '';
    const parts = fullPath.split('/');
    return parts[parts.length - 1];
  },
  
  getFilePath(fullPath) {
    if (!fullPath) return '/';
    const parts = fullPath.split('/');
    if (parts.length <= 1) return '/';
    return parts.slice(0, -1).join('/') + '/';
  },
    // Setup intersection observer for infinite scroll
setupInfiniteScroll() {
  this.$nextTick(() => {
    // Clean up old observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    
    // Find the scroll trigger element
    const trigger = document.querySelector('.scroll-trigger');
    if (!trigger) {
      // If no trigger, we might not have enough results yet
      return;
    }
    
    // Get the container (the search results container)
    const container = this.$refs.searchResultsContainer;
    
    // Create new observer
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && this.hasMoreResults && !this.isLoadingMore) {
            this.loadMoreResults();
          }
        });
      },
      {
        root: container,
        threshold: 0.1,
        rootMargin: '50px' // Start loading a bit earlier
      }
    );
    
    this.intersectionObserver.observe(trigger);
  });
},
  
  // Load more results
  loadMoreResults() {
    if (this.isLoadingMore || !this.hasMoreResults) return;
    
    this.isLoadingMore = true;
    
    // Simulate loading delay for smooth UX
    setTimeout(() => {
      this.visibleResultCount += this.resultsPerScroll;
      this.isLoadingMore = false;
      
      // Re-setup observer for new trigger
      this.$nextTick(() => {
        this.setupInfiniteScroll();
      });
    }, 300);
  },
  
  // Reset infinite scroll when search changes
  resetInfiniteScroll() {
    this.visibleResultCount = 20;
    this.isLoadingMore = false;
    
    this.$nextTick(() => {
      if (this.showSearchResults) {
        this.setupInfiniteScroll();
      }
    });
  },
    debugSearch() {
  console.log('=== SEARCH DEBUG ===');
  console.log('searchQuery:', this.searchQuery);
  console.log('allFiles length:', this.allFiles.length);
  
  if (this.allFiles.length > 0) {
    console.log('First file sample:', {
      name: this.allFiles[0].name,
      hasContent: !!this.allFiles[0].content,
      contentLength: this.allFiles[0].content?.length,
      hasErrors: !!this.allFiles[0].errors,
      hasWarnings: !!this.allFiles[0].warnings
    });
    
    // List all filenames
    console.log('All filenames:', this.allFiles.map(f => f.name));
  }
  
  console.log('todoComments length:', this.todoComments.length);
  console.log('========================');
},
    // Create debounced search function
  debouncedSearch: debounce(function(query) {
    this.performSearch(query);
  }, 300),

performSearch(query) {
  this.isSearching = true;
  
  if (!query || query.trim() === '') {
    this.enhancedSearchResults = [];
    this.searchResults = [];
    this.isSearching = false;
    return;
  }
  
  const queryLower = query.toLowerCase().trim();
  const results = [];
  
  this.allFiles.forEach(file => {
    let matchFound = false;
    let matchType = '';
    let matchPreview = '';
    
    // Search in filename
    if (file.name.toLowerCase().includes(queryLower)) {
      matchFound = true;
      matchType = 'filename';
      
      // Create preview safely
      try {
        const pathParts = file.name.split('/');
        const matchingPart = pathParts.find(part => 
          part && part.toLowerCase().includes(queryLower)
        );
        if (matchingPart) {
          const index = matchingPart.toLowerCase().indexOf(queryLower);
          if (index !== -1) {
            const preview = matchingPart.substring(0, index) + 
                           '<strong>' + matchingPart.substring(index, index + query.length) + '</strong>' + 
                           matchingPart.substring(index + query.length);
            matchPreview = `.../${preview}`;
          }
        }
      } catch (e) {
        console.warn('Error creating filename preview:', e);
      }
    }
    
    // Search in content
    if (!matchFound && file.content && file.content.toLowerCase().includes(queryLower)) {
      matchFound = true;
      matchType = 'content';
      
      try {
        const lines = file.content.split('\n');
        for (let i = 0; i < Math.min(lines.length, 50); i++) {
          const line = lines[i];
          if (line && line.toLowerCase().includes(queryLower)) {
            const lineNum = i + 1;
            const trimmedLine = line.trim();
            const matchIndex = trimmedLine.toLowerCase().indexOf(queryLower);
            if (matchIndex !== -1) {
              const start = Math.max(0, matchIndex - 20);
              const end = Math.min(trimmedLine.length, matchIndex + query.length + 20);
              let preview = trimmedLine.substring(start, end);
              
              // Highlight the match
              const previewLower = preview.toLowerCase();
              const previewMatchIndex = previewLower.indexOf(queryLower);
              if (previewMatchIndex !== -1) {
                preview = preview.substring(0, previewMatchIndex) +
                         '<strong>' + preview.substring(previewMatchIndex, previewMatchIndex + query.length) + '</strong>' +
                         preview.substring(previewMatchIndex + query.length);
              }
              
              matchPreview = `Line ${lineNum}: ${preview}...`;
            }
            break;
          }
        }
      } catch (e) {
        console.warn('Error creating content preview:', e);
      }
    }
    
    if (matchFound) {
      results.push({
        ...file,
        matchType,
        matchPreview,
        // Store these for display, but don't call methods here
        displayPath: file.name,
        fileName: file.name.split('/').pop() || file.name
      });
    }
  });
  
  // Sort results
  results.sort((a, b) => {
    if (a.matchType === 'filename' && b.matchType !== 'filename') return -1;
    if (b.matchType === 'filename' && a.matchType !== 'filename') return 1;
    if (a.matchType === 'content' && b.matchType !== 'content') return -1;
    if (b.matchType === 'content' && a.matchType !== 'content') return 1;
    return 0;
  });
  
  this.enhancedSearchResults = results;
  this.searchResults = results;
  this.visibleResultCount = 20;
  
  // Auto-show results
  if (results.length > 0) {
    this.showSearchResults = true;
  }
    // Reset and setup infinite scroll for new results
  this.$nextTick(() => {
    this.setupInfiniteScroll();
  });
  this.isSearching = false;
},
  
  getMatchTypeColor(type) {
    const colors = {
      filename: 'primary',
      content: 'success',
      todo: 'warning',
      error: 'error',
      warning: 'orange'
    };
    return colors[type] || 'grey';
  },
  
  navigateToFile(file) {
    // Find and highlight in tree view
    if (this.projectTree) {
      // Expand all folders leading to this file
      const parts = file.name.split('/');
      let currentPath = '';
      const nodesToExpand = [];
      
      for (let i = 0; i < parts.length - 1; i++) {
        currentPath += (i > 0 ? '/' : '') + parts[i];
        nodesToExpand.push(`folder-${currentPath}`);
      }
      
      this.openNodes = [...new Set([...this.openNodes, ...nodesToExpand])];
      
      // Scroll to the file in tree (you'll need to implement this)
      this.$nextTick(() => {
        const element = document.querySelector(`[data-id="file-${file.name}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('highlight-file');
          setTimeout(() => element.classList.remove('highlight-file'), 2000);
        }
      });
    }
    
    // Switch to structure tab
    this.activeTab = 3; // Structure tab index
    
    // Close results
    this.showSearchResults = false;
  },
  
  copyPath(path) {
    navigator.clipboard.writeText(path).then(() => {
      this.showSuccessMsg('Path copied to clipboard');
    }).catch(() => {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = path;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.showSuccessMsg('Path copied to clipboard');
    });
  },

  autoExpandTreeNodes(results) {
    const pathsToExpand = new Set();
    
    results.forEach(file => {
      const parts = file.name.split('/');
      let currentPath = '';
      
      for (let i = 0; i < parts.length - 1; i++) {
        currentPath += (i > 0 ? '/' : '') + parts[i];
        pathsToExpand.add(`folder-${currentPath}`);
      }
    });
    
    this.openNodes = [...new Set([...this.openNodes, ...pathsToExpand])];
  },

  // ===== 2. PROGRESS PERSISTENCE METHODS =====
  
saveProgress(showNotification = true) {
  const progress = {
    selectedTypes: this.selectedTypes,
    countEmptyLines: this.countEmptyLines,
    ignoreComments: this.ignoreComments,
    thoroughAnalysis: this.thoroughAnalysis,
    analyzeDocumentation: this.analyzeDocumentation,
    analyzeDependencies: this.analyzeDependencies,
    lastSaved: new Date().toISOString(),
    version: '1.0'
  };
  
  localStorage.setItem('codeAnalyzerProgress', JSON.stringify(progress));
  this.lastSaved = new Date();
  
  // Also save analysis results if complete
  if (this.analysisComplete) {
    const results = {
      totalLines: this.totalLines,
      totalFilesProcessed: this.totalFilesProcessed,
      fileTypeSummary: this.fileTypeSummary,
      complexityMetrics: this.complexityMetrics,
      duplicates: this.duplicates.length,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('codeAnalyzerLastResults', JSON.stringify(results));
  }
  
  // Only show notification for manual saves
  if (showNotification) {
    this.showSuccessMsg('Progress saved to browser');
  }
},

  loadProgress(showNotification = true) {
    const saved = localStorage.getItem('codeAnalyzerProgress');
    if (saved) {
      try {
        const progress = JSON.parse(saved);
        
        if (progress.version === '1.0') {
          this.selectedTypes = progress.selectedTypes || [];
          this.countEmptyLines = progress.countEmptyLines || false;
          this.ignoreComments = progress.ignoreComments !== undefined ? 
            progress.ignoreComments : true;
          this.thoroughAnalysis = progress.thoroughAnalysis !== undefined ? 
            progress.thoroughAnalysis : true;
          this.analyzeDocumentation = progress.analyzeDocumentation || false;
          this.analyzeDependencies = progress.analyzeDependencies || false;
          
          const savedDate = new Date(progress.lastSaved);
          if (showNotification){
          this.showSuccessMsg(`Loaded settings from ${savedDate.toLocaleString()}`);
          }
        }
      } catch (e) {
        console.error('Failed to load saved progress:', e);
        this.showErrorMsg('Failed to load saved progress');
      }
    } else {
      if (showNotification){
      this.showWarningMsg('No saved progress found');
      }
    }
  },

  clearSavedProgress() {
    localStorage.removeItem('codeAnalyzerProgress');
    localStorage.removeItem('codeAnalyzerLastResults');
    this.lastSaved = null;
    this.showSuccessMsg('Saved progress cleared');
  },

  startAutoSave() {
    if (this.autoSaveId) {
      clearInterval(this.autoSaveId);
    }
    
    this.autoSaveId = setInterval(() => {
      if (this.hasSelectedFile || this.selectedTypes.length) {
        this.saveProgress(false); // Don't show notification for auto-saves
      }
    }, 30000); // Save every 30 seconds
  },

  stopAutoSave() {
    if (this.autoSaveId) {
      clearInterval(this.autoSaveId);
      this.autoSaveId = null;
    }
  },

  // ===== 3. CODE CHURN ANALYSIS METHODS =====
  
  generateMockHistory(files) {
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;
    
    files.forEach(file => {
      const changes = [];
      // More complex files get more changes
      const fileObj = this.complexFiles.find(f => f.name === file.name);
      const complexity = fileObj?.complexity || 5;
      const changeCount = Math.floor(Math.random() * complexity) + 1;
      
      for (let i = 0; i < changeCount; i++) {
        changes.push({
          date: new Date(now - Math.random() * 90 * day).toISOString(), // Last 90 days
          linesAdded: Math.floor(Math.random() * 50) + 1,
          linesRemoved: Math.floor(Math.random() * 30),
          author: ['dev1', 'dev2', 'dev3', 'dev4'][Math.floor(Math.random() * 4)]
        });
      }
      
      changes.sort((a, b) => new Date(b.date) - new Date(a.date));
      this.fileHistory.set(file.name, changes);
    });
  },

analyzeCodeChurn(files) {
  if (!files || files.length === 0) return;

  const highChurnFiles = [];
  const churnByType = {};
  let totalVolatilityScore = 0;

  files.forEach(file => {
    const ext = file.extension || this.getFileExtension(file.name);
    const code = file.content || '';
    const lines = file.lines || 1;

    // Signal 1: TODO/FIXME density — unfinished work
    const todoCount = (code.match(/\/\/\s*(TODO|FIXME|HACK|BUG)/gi) || []).length;
    const todoDensity = todoCount / (lines / 100); // per 100 lines

    // Signal 2: Commented-out code blocks — code that was changed and left behind
    // FIXED: Removed unnecessary escapes inside character class
    const commentedCodeLines = code.split('\n').filter(line => {
      const t = line.trim();
      // A comment that looks like it contains code (not prose)
      // No need to escape (, ), {, } inside [(){};=]
      return (t.startsWith('//') || t.startsWith('#')) &&
             /[(){};=]/.test(t) && // Fixed: removed backslashes
             t.length > 10;
    }).length;
    const commentedCodeDensity = commentedCodeLines / (lines / 100);

    // Signal 3: Complexity relative to file size — high complexity in small files
    // suggests the file has been patched many times without refactoring
    const complexity = file.complexity || 0;
    const complexityDensity = lines > 0 ? complexity / (lines / 50) : 0;

    // Signal 4: Is this file a known duplicate? — copied code tends to be unstable
    const isDuplicated = this.duplicates && this.duplicates.some(
      d => d.original === file.name || (d.duplicates && d.duplicates.includes(file.name))
    );

    // Combine into a volatility score (0–100)
    const score = Math.min(100, Math.round(
      (todoDensity * 20) +
      (commentedCodeDensity * 15) +
      (complexityDensity * 10) +
      (isDuplicated ? 20 : 0)
    ));

    if (!churnByType[ext]) churnByType[ext] = 0;
    churnByType[ext] = Math.max(churnByType[ext], score);

    totalVolatilityScore += score;

    if (score > 25) {
      highChurnFiles.push({
        name: file.name,
        score,
        todoCount,
        commentedCodeLines,
        complexity,
        isDuplicated,
      });
    }
  });

  const avgScore = files.length > 0 ? totalVolatilityScore / files.length : 0;
  const stabilityScore = Math.max(0, Math.round(100 - avgScore));

  this.codeChurn = {
    highChurnFiles: highChurnFiles.sort((a, b) => b.score - a.score).slice(0, 10),
    churnByType,
    stabilityScore,
    totalVolatilityScore: Math.round(totalVolatilityScore),
  };
},

  // ===== 4. TECH DEBT ESTIMATION METHODS =====
  
calculateTechDebt() {
  const debt = {
    total: 0,
    perCategory: {
      complexity: 0,
      duplication: 0,
      documentation: 0,
      dependencies: 0,
      codeSmells: 0
    },
    recommendations: []
  };
  
  // Dynamic max based on project size
  // 1-2 hours per 1000 lines of code, with reasonable min/max
  const projectSizeHours = Math.max(10, Math.min(160, Math.round(this.totalLines / 500)));
  const MAX_REASONABLE_HOURS = Math.min(160, projectSizeHours);
  
  console.log('Project size:', this.totalLines, 'lines -> max hours:', MAX_REASONABLE_HOURS);
  
  // Track if we found any issues
  let issuesFound = 0;
  
  // 1. Complexity Debt - Only flag top 20% most complex files
  const complexityThreshold = this.getComplexityThreshold(this.complexFiles);
  this.complexFiles.forEach(file => {
    if (file.complexity > complexityThreshold && file.complexity > 10) {
      issuesFound++;
      // 0.5-2 hours based on how far above threshold
      const complexityDebt = Math.min(2, (file.complexity - complexityThreshold) / 5);
      
      debt.total += complexityDebt;
      debt.perCategory.complexity += complexityDebt;
      
      debt.recommendations.push({
        category: 'complexity',
        file: file.name,
        message: `High complexity (${file.complexity}) - consider refactoring`,
        effort: complexityDebt > 1.5 ? 'medium' : 'low',
        hours: Math.round(complexityDebt * 2) / 2
      });
    }
  });
  
  // 2. Duplication Debt - Scale by project size
  this.duplicates.forEach(dup => {
    issuesFound++;
    // 0.5-3 hours based on lines duplicated
    const duplicateDebt = Math.min(3, (dup.lines / 100) * 0.5);
    
    debt.total += duplicateDebt;
    debt.perCategory.duplication += duplicateDebt;
    
    debt.recommendations.push({
      category: 'duplication',
      file: dup.original,
      message: `${dup.lines} lines duplicated across ${dup.duplicates.length} files`,
      effort: duplicateDebt > 2 ? 'medium' : 'low',
      hours: Math.round(duplicateDebt * 2) / 2
    });
  });
  
  // 3. Documentation Debt - Only flag large files with poor docs
  this.allFiles.forEach(file => {
    if (file.lines > 200) {
      const commentRatio = this.calculateCommentRatio(file);
      if (commentRatio < 5) {
        issuesFound++;
        const docDebt = Math.min(2, file.lines / 500);
        
        debt.total += docDebt;
        debt.perCategory.documentation += docDebt;
        
        debt.recommendations.push({
          category: 'documentation',
          file: file.name,
          message: `Poor documentation (${commentRatio.toFixed(1)}% comments) in ${file.lines} line file`,
          effort: 'low',
          hours: Math.round(docDebt * 2) / 2
        });
      }
    }
  });
  
  // 4. Circular Dependencies - Each cycle is significant
  this.circularDependencies.forEach(circle => {
    issuesFound++;
    const circularDebt = Math.min(4, circle.length * 0.75);
    
    debt.total += circularDebt;
    debt.perCategory.dependencies += circularDebt;
    
    debt.recommendations.push({
      category: 'dependencies',
      files: circle,
      message: `Circular dependency affecting ${circle.length} files`,
      effort: circularDebt > 2.5 ? 'high' : circularDebt > 1.5 ? 'medium' : 'low',
      hours: Math.round(circularDebt * 2) / 2
    });
  });
  
  // 5. Code Smells - Only high severity, scale by occurrence
  if (this.errorSummary && this.errorSummary.byType) {
    Object.values(this.errorSummary.byType).forEach(issue => {
      if (issue.severity === 'high' && issue.count > 2) {
        issuesFound++;
        const smellDebt = Math.min(2, issue.count * 0.2);
        
        debt.total += smellDebt;
        debt.perCategory.codeSmells += smellDebt;
        
        debt.recommendations.push({
          category: 'codeSmells',
          message: `${issue.count} instances of: ${issue.message}`,
          effort: smellDebt > 1 ? 'medium' : 'low',
          hours: Math.round(smellDebt * 2) / 2
        });
      }
    });
  }
  
  // If no issues found, set a minimal baseline based on project size
  if (issuesFound === 0) {
    debt.total = Math.max(1, Math.min(8, Math.round(this.totalLines / 1000)));
  }
  
  // Round all values
  debt.total = Math.min(MAX_REASONABLE_HOURS, Math.round(debt.total * 10) / 10);
  Object.keys(debt.perCategory).forEach(key => {
    debt.perCategory[key] = Math.round(debt.perCategory[key] * 10) / 10;
  });
  
  // Calculate debt score based on project-specific max
  const debtScore = MAX_REASONABLE_HOURS > 0 
    ? Math.max(0, Math.min(100, 100 - ((debt.total / MAX_REASONABLE_HOURS) * 100)))
    : 100;
  
  // Sort and limit recommendations
  debt.recommendations.sort((a, b) => b.hours - a.hours);
  
  this.techDebt = {
    total: debt.total,
    perCategory: debt.perCategory,
    recommendations: debt.recommendations.slice(0, 10),
    debtScore: Math.round(debtScore),
    estimatedHours: Math.ceil(debt.total)
  };
  
  console.log('Tech Debt:', {
    totalLines: this.totalLines,
    maxHours: MAX_REASONABLE_HOURS,
    actualHours: this.techDebt.total,
    score: this.techDebt.debtScore,
    issuesFound
  });
  
  return this.techDebt;
},

getComplexityThreshold(complexFiles) {
  if (!complexFiles || complexFiles.length === 0) return 15;
  
  const complexities = complexFiles.map(f => f.complexity).sort((a, b) => a - b);
  const index = Math.floor(complexities.length * 0.8); // 80th percentile
  return Math.max(10, complexities[index] || 15);
},

  calculateCommentRatio(file) {
    if (!file.content) return 0;
    
    const lines = file.content.split('\n');
    const commentLines = lines.filter(line => 
      line.trim().startsWith('//') || 
      line.trim().startsWith('#') || 
      line.trim().startsWith('/*') ||
      line.trim().startsWith('*')
    ).length;
    
    return lines.length > 0 ? (commentLines / lines.length) * 100 : 0;
  },

  // ===== UI HELPER METHODS =====
  
  getDebtScoreColor(score) {
    if (score >= 80) return 'success';
    if (score >= 60) return 'info';
    if (score >= 40) return 'warning';
    return 'error';
  },

  getChurnColor(stability) {
    if (stability >= 80) return 'success--text';
    if (stability >= 60) return 'info--text';
    if (stability >= 40) return 'warning--text';
    return 'error--text';
  },

  getCategoryColor(category) {
    const colors = {
      complexity: 'error',
      duplication: 'warning',
      documentation: 'info',
      dependencies: 'purple',
      codeSmells: 'orange'
    };
    return colors[category] || 'grey';
  },

  getRecommendationColor(effort) {
    const colors = {
      high: 'error',
      medium: 'warning',
      low: 'info'
    };
    return colors[effort] || 'grey';
  },

  getRecommendationIcon(effort) {
    const icons = {
      high: 'mdi-alert-circle',
      medium: 'mdi-alert',
      low: 'mdi-information'
    };
    return icons[effort] || 'mdi-lightbulb-on';
  },

  getEffortColor(effort) {
    const colors = {
      high: 'error',
      medium: 'warning',
      low: 'success'
    };
    return colors[effort] || 'grey';
  },

  handleBeforeUnload(e) {
    console.log(e)
    if (this.hasSelectedFile || this.selectedTypes.length) {
      this.saveProgress(false);
    }
    
  },
    // Tree view pagination
prevTreePage() {
  if (this.treePage > 1) this.treePage--;
},

nextTreePage() {
  if (this.treePage < this.treeTotalPages) this.treePage++;
},

// Files list pagination
prevFilesListPage() {
  if (this.filesListPage > 1) this.filesListPage--;
},

nextFilesListPage() {
  if (this.filesListPage < this.filesListTotalPages) this.filesListPage++;
},

// Matrix view pagination
prevMatrixPage() {
  if (this.matrixPage > 1) this.matrixPage--;
},

nextMatrixPage() {
  if (this.matrixPage < this.matrixTotalPages) this.matrixPage++;
},

// Circular dependencies pagination
prevCircularPage() {
  if (this.circularPage > 1) this.circularPage--;
},

nextCircularPage() {
  if (this.circularPage < this.circularTotalPages) this.circularPage++;
},
    // Original methods (all unchanged)
    browseFiles() {
      this.$refs.file.click();
    },
    
    browseFolder() {
      this.$refs.folder.click();
    },
      showFileIssues(file) {
    this.selectedFileForIssues = file;
    this.showIssuesDialog = true;
  },
async captureHealthSection() {
  this.isCapturingScreenshot = true;
  
  try {
    const healthSection = document.querySelector('.health-metrics-section');
    if (!healthSection) {
      throw new Error('Health section not found');
    }
    
    // Store original styles
    const originalStyles = {
      bg: healthSection.style.backgroundColor,
      color: healthSection.style.color
    };
    
    // Force white background and black text for screenshot
    healthSection.style.backgroundColor = '#ffffff';
    healthSection.style.color = '#000000';
    
    // Force all child elements to have visible text
    const allElements = healthSection.querySelectorAll('*');
    const originalElementStyles = [];
    
    allElements.forEach((el, index) => {
      originalElementStyles[index] = {
        color: el.style.color,
        bg: el.style.backgroundColor
      };
      
      // Override for screenshot
      if (el.tagName === 'DIV' || el.tagName === 'SPAN' || el.tagName === 'P') {
        el.style.color = '#000000';
      }
      if (el.classList.contains('v-chip')) {
        el.style.color = '#ffffff'; // Keep chips white text
      }
      if (el.classList.contains('text--secondary')) {
        el.style.color = '#666666'; // Keep secondary text visible but lighter
      }
    });
    
    const canvas = await html2canvas(healthSection, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      allowTaint: true,
      useCORS: true
    });
    
    // Restore original styles
    healthSection.style.backgroundColor = originalStyles.bg;
    healthSection.style.color = originalStyles.color;
    
    allElements.forEach((el, index) => {
      el.style.color = originalElementStyles[index].color;
      el.style.backgroundColor = originalElementStyles[index].bg;
    });
    
    return canvas;
  } catch (error) {
    console.error('Screenshot failed:', error);
    return null;
  } finally {
    this.isCapturingScreenshot = false;
  }
},

async shareToPlatform(platform) {
  this.shareMenu = false;
  
  switch(platform) {
    case 'twitter':
      await this.shareToTwitter();
      break;
    case 'reddit':
      await this.shareToReddit();
      break;
    case 'linkedin':
      await this.shareToLinkedIn();
      break;
    case 'instagram':
      await this.shareToInstagram();
      break;
    case 'copy':
      await this.copyStats();
      break;
  }
},

async shareToTwitter() {
  const text = encodeURIComponent(this.shareableText.substring(0, 240) + '...');
  const url = encodeURIComponent(this.shareableUrl);
  window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
},

async shareToReddit() {
  const title = encodeURIComponent(`Code Health Score: ${this.overallHealthScore}/100`);
  const url = encodeURIComponent(this.shareableUrl);
  window.open(`https://www.reddit.com/submit?title=${title}&url=${url}`, '_blank');
},

async shareToLinkedIn() {
  const title = encodeURIComponent(`Code Health Score: ${this.overallHealthScore}/100`);
  console.log('LinkedIn share title:', title);
  const summary = encodeURIComponent(this.shareableText.substring(0, 200));
  console.log('LinkedIn share text:', summary);
  const url = encodeURIComponent(this.shareableUrl);
  window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
},

// Add this method to your component for Instagram-optimized screenshots
async captureForInstagram() {
  this.isCapturingScreenshot = true;
  
  try {
    const healthSection = document.querySelector('.health-metrics-section');
    if (!healthSection) {
      throw new Error('Health section not found');
    }
    
    // Clone the section for Instagram-optimized capture
    const clone = healthSection.cloneNode(true);
    clone.style.position = 'absolute';
    clone.style.left = '-9999px';
    clone.style.top = '0';
    clone.style.width = '1080px'; // Instagram optimal width (1080x1080 square)
    clone.style.backgroundColor = '#ffffff';
    clone.style.padding = '40px';
    clone.style.borderRadius = '24px';
    clone.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
    clone.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    
    // Force dark text on white background for ALL elements
    clone.style.color = '#000000';
    
    // Override all child elements to ensure visibility
    const allElements = clone.querySelectorAll('*');
    allElements.forEach(el => {
      // Reset any inline styles that might affect text color
      if (el.style) {
        // Keep background colors but ensure text is dark
        if (el.classList.contains('v-chip') || el.classList.contains('primary') || 
            el.classList.contains('success') || el.classList.contains('warning') || 
            el.classList.contains('error') || el.classList.contains('info')) {
          // Keep chip colors but ensure text is white
          el.style.color = '#ffffff';
        } else {
          // For all other elements, use dark text
          el.style.color = '#000000';
        }
        
        // Override any theme-specific classes
        if (el.classList.contains('text--secondary')) {
          el.style.color = '#666666';
        }
        
        if (el.classList.contains('text--primary')) {
          el.style.color = '#1976d2';
        }
      }
    });
    
    // Style progress circles
    const progressCircles = clone.querySelectorAll('.v-progress-circular');
    progressCircles.forEach(circle => {
      const content = circle.querySelector('.v-progress-circular__content');
      if (content) {
        content.style.color = '#000000';
        content.style.fontWeight = 'bold';
      }
    });
    
    // Add Instagram-style gradient header
    const header = document.createElement('div');
    header.style.background = 'linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)';
    header.style.color = 'white';
    header.style.padding = '40px 40px 30px 40px';
    header.style.margin = '-40px -40px 30px -40px';
    header.style.borderRadius = '24px 24px 0 0';
    header.style.textAlign = 'center';
    header.style.fontWeight = 'bold';
    header.style.boxShadow = '0 4px 20px rgba(0,0,0,0.2)';
    header.innerHTML = `
      <div style="font-size: 36px; margin-bottom: 20px;">📊 Code Health Report</div>
      <div style="font-size: 72px; font-weight: 800; line-height: 1.2; margin-bottom: 10px;">
        ${this.overallHealthScore}/100
      </div>
      <div style="font-size: 20px; opacity: 0.9; letter-spacing: 1px;">
        OVERALL HEALTH SCORE
      </div>
    `;
    
    clone.insertBefore(header, clone.firstChild);
    
    // Add metric values with better visibility
    const metricValues = clone.querySelectorAll('.v-progress-circular__content');
    metricValues.forEach(el => {
      el.style.fontSize = '18px';
      el.style.fontWeight = 'bold';
    });
    
    // Add Instagram-style footer with URL and hashtags
    const footer = document.createElement('div');
    footer.style.marginTop = '30px';
    footer.style.padding = '25px';
    footer.style.background = 'linear-gradient(135deg, #f5f5f5, #ffffff)';
    footer.style.borderRadius = '16px';
    footer.style.border = '1px solid #e0e0e0';
    footer.style.textAlign = 'center';
    footer.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 15px;">
        <span style="background: #1976d2; color: white; padding: 8px 20px; border-radius: 30px; font-size: 16px; font-weight: 500;">
          🔗 ${window.location.origin}
        </span>
      </div>
      <div style="color: #666666; font-size: 14px; margin-bottom: 10px;">
        Analyze your code quality and get detailed insights
      </div>
      <div style="color: #1976d2; font-size: 14px; font-weight: 500;">
        #CodeHealth #DevLife #CodeQuality #Programming #Tech
      </div>
      <div style="color: #999999; font-size: 12px; margin-top: 15px;">
        Generated on ${new Date().toLocaleDateString()}
      </div>
    `;
    
    clone.appendChild(footer);
    
    document.body.appendChild(clone);
    
    // Wait for fonts and styles to apply
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Capture with html2canvas
    const canvas = await html2canvas(clone, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      allowTaint: true,
      useCORS: true,
      onclone: (clonedDoc) => {
        const clonedElement = clonedDoc.querySelector('[style*="left: -9999px"]');
        if (clonedElement) {
          // Final visibility checks in the cloned document
          const texts = clonedElement.querySelectorAll('div, span, p, h1, h2, h3, h4');
          texts.forEach(el => {
            if (el.classList.contains('v-chip')) {
              el.style.color = '#ffffff';
            } else if (!el.style.color || el.style.color === 'rgba(0, 0, 0, 0)') {
              el.style.color = '#000000';
            }
          });
        }
      }
    });
    
    document.body.removeChild(clone);
    
    return canvas;
  } catch (error) {
    console.error('Instagram capture failed:', error);
    this.showErrorMsg('Failed to capture Instagram screenshot');
    return null;
  } finally {
    this.isCapturingScreenshot = false;
  }
},

// Update the shareToInstagram method to use the new capture function
async shareToInstagram() {
  const canvas = await this.captureForInstagram();
  if (canvas) {
    // Create download link
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = `code-health-instagram-${new Date().getTime()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Also create a preview dialog option
    this.shareImageUrl = url;
    this.shareDialog = true;
    
    this.showSuccessMsg(`
      📸 Instagram-ready screenshot created!
      
      Tips for posting:
      • Upload this image to Instagram
      • Add the stats in your caption
      • Tag #CodeHealth #DevLife #CodeQuality
    `);
  }
},

async copyStats() {
  try {
    await navigator.clipboard.writeText(this.shareableText);
    this.showSuccessMsg('Stats copied to clipboard! 📋');
  } catch (err) {
    // Fallback
    const textArea = document.createElement('textarea');
    textArea.value = this.shareableText;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    this.showSuccessMsg('Stats copied to clipboard! 📋');
  }
},

// Enhanced share method that shows the dropdown
showShareOptions() {
  this.shareMenu = true;
},
    shareHealthScore() {
  const healthData = this.shareableHealthData;
  
  // Create share text
  const shareText = `📊 Code Health Score: ${healthData.overallScore}/100\n` +
    `📁 Files: ${healthData.project.totalFiles}\n` +
    `📝 Lines: ${healthData.project.totalLines.toLocaleString()}\n` +
    `⚡ Avg Complexity: ${healthData.project.avgComplexity}\n` +
    `🔄 Duplicates: ${healthData.duplicates}\n` +
    `⏰ TODOs: ${healthData.todoComments}\n\n` +
    `Metrics:\n` +
    healthData.metrics.map(m => `  • ${m.name}: ${m.value}%`).join('\n');
  
  // Try to use Web Share API if available
  if (navigator.share) {
    navigator.share({
      title: 'Code Health Score',
      text: shareText,
      url: window.location.href
    }).catch(() => {
      // Fallback to clipboard
      this.copyHealthToClipboard(shareText, healthData);
    });
  } else {
    // Fallback to clipboard
    this.copyHealthToClipboard(shareText, healthData);
  }
},

copyHealthToClipboard(text, healthData) {
  navigator.clipboard.writeText(text).then(() => {
    this.showSuccessMsg('Health score copied to clipboard! 📋');
  }).catch(() => {
    // If clipboard fails, offer JSON export
    this.exportHealthJSON(healthData);
  });
},

exportHealthJSON(healthData) {
  const dataStr = JSON.stringify(healthData, null, 2);
  this.downloadFile(dataStr, 'code-health.json', 'application/json');
  this.showSuccessMsg('Health data exported as JSON');
},

// Add this to the export methods
exportHealthReport() {
  const healthData = this.shareableHealthData;
  
  // Create a simple HTML report
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Code Health Report</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 40px; }
    h1 { color: #1976d2; }
    .score { font-size: 48px; font-weight: bold; text-align: center; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .score.excellent { background: #4caf50; color: white; }
    .score.good { background: #2196f3; color: white; }
    .score.fair { background: #ff9800; color: white; }
    .score.poor { background: #f44336; color: white; }
    .metrics { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin: 20px 0; }
    .metric { background: #f5f5f5; padding: 15px; border-radius: 8px; text-align: center; }
    .metric-value { font-size: 24px; font-weight: bold; color: #1976d2; }
    .details { margin-top: 30px; }
    table { width: 100%; border-collapse: collapse; }
    td { padding: 8px; border-bottom: 1px solid #ddd; }
  </style>
</head>
<body>
  <h1>Code Health Report</h1>
  <p>Generated on ${new Date().toLocaleString()}</p>
  
  <div class="score ${this.getHealthScoreClass(healthData.overallScore)}">
    Overall Health Score: ${healthData.overallScore}/100
  </div>
  
  <div class="metrics">
    ${healthData.metrics.map(m => `
      <div class="metric">
        <div class="metric-value">${m.value}%</div>
        <div>${m.name}</div>
      </div>
    `).join('')}
  </div>
  
  <div class="details">
    <h3>Project Statistics</h3>
    <table>
      <tr><td>Total Files</td><td>${healthData.project.totalFiles}</td></tr>
      <tr><td>Total Lines</td><td>${healthData.project.totalLines.toLocaleString()}</td></tr>
      <tr><td>Average Complexity</td><td>${healthData.project.avgComplexity}</td></tr>
      <tr><td>Duplicate Blocks</td><td>${healthData.duplicates}</td></tr>
      <tr><td>TODO/FIXME Comments</td><td>${healthData.todoComments}</td></tr>
      <tr><td>Circular Dependencies</td><td>${healthData.circularDependencies}</td></tr>
    </table>
  </div>
</body>
</html>`;
  
  this.downloadFile(html, 'code-health-report.html', 'text/html');
  this.showSuccessMsg('Health report generated!');
},

getHealthScoreClass(score) {
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'fair';
  return 'poor';
},
 // Pagination methods for issues
  prevIssuePage() {
    if (this.issuePage > 1) this.issuePage--;
  },
  
  nextIssuePage() {
    if (this.issuePage < this.issueTotalPages) this.issuePage++;
  },
  
  // Pagination methods for files
  prevFilesPage() {
    if (this.filesPage > 1) this.filesPage--;
  },
  
  nextFilesPage() {
    if (this.filesPage < this.filesTotalPages) this.filesPage++;
  },
  
  // Reset pagination when new analysis is loaded
 resetPagination() {
  this.issuePage = 1;
  this.filesPage = 1;
  this.treePage = 1;
  this.filesListPage = 1;
  this.matrixPage = 1;
  this.circularPage = 1;
},
  
  getSeverityColor(severity) {
    switch(severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'grey';
    }
  },
onFileSelected(e) {
  const file = e.target.files[0];
  if (file) {
    // USE SECURITY_CONFIG here
    if (isSuspiciousFilename(file.name)) {
      this.showErrorMsg('Suspicious file name detected');
      return;
    }
    
    // Use the config constant
    if (!isValidFileType(file.name)) {
      this.showWarningMsg(`File type may not be analyzable. Allowed types: ${SECURITY_CONFIG.ALLOWED_EXTENSIONS.join(', ')}`);
    }
    
    if (file.size > MAX_FILE_SIZE) {
      this.showErrorMsg(`File size must be less than ${this.formatFileSize(MAX_FILE_SIZE)}`);
      return;
    }
    this.selectedFile = file;
    this.selectedFolder = null;
    this.analysisComplete = false;
    this.resetFileInputs();
    this.checkAndShowFileSizeWarning(file.size);
  }
},
    
onFolderSelected(e) {
  const files = Array.from(e.target.files || []);
  if (files.length > 0) {
    // Check if folder exceeds file count limit
    if (files.length > SECURITY_CONFIG.MAX_TOTAL_FILES) {
      this.showErrorMsg(`Too many files. Maximum allowed: ${SECURITY_CONFIG.MAX_TOTAL_FILES}`);
      return;
    }
    
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    // USE MAX_FOLDER_SIZE here instead of MAX_FILE_SIZE
    if (totalSize > MAX_FOLDER_SIZE) {
      this.showErrorMsg(`Total folder size must be less than ${this.formatFileSize(MAX_FOLDER_SIZE)}`);
      return;
    }
    this.selectedFolder = files;
    this.selectedFile = null;
    this.analysisComplete = false;
    this.resetFileInputs();
    this.checkAndShowFileSizeWarning(totalSize);
  }
},
handleDrop(e) {
  e.preventDefault();
  this.isDragging = false;
  
  const items = Array.from(e.dataTransfer.items);
  const files = [];
  
  for (const item of items) {
    if (item.kind === 'file') {
      const file = item.getAsFile();
      if (file) {
        files.push(file);
      }
    }
  }
  
  if (files.length > 0) {
    const zipFile = files.find(f => 
      f.name.match(/\.(zip|7z|tar\.gz)$/i)
    );
    
    if (zipFile && files.length === 1) {
      if (zipFile.size > MAX_FILE_SIZE) {
        this.showErrorMsg(`File size must be less than ${this.formatFileSize(MAX_FILE_SIZE)}`);
        return;
      }
      this.selectedFile = zipFile;
      this.selectedFolder = null;
      this.checkAndShowFileSizeWarning(zipFile.size);
    } else {
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);
      // USE MAX_FOLDER_SIZE here
      if (totalSize > MAX_FOLDER_SIZE) {
        this.showErrorMsg(`Total size must be less than ${this.formatFileSize(MAX_FOLDER_SIZE)}`);
        return;
      }
      
      // Also check file count
      if (files.length > SECURITY_CONFIG.MAX_TOTAL_FILES) {
        this.showErrorMsg(`Too many files. Maximum allowed: ${SECURITY_CONFIG.MAX_TOTAL_FILES}`);
        return;
      }
      
      this.selectedFolder = files;
      this.selectedFile = null;
      this.checkAndShowFileSizeWarning(totalSize);
    }
    this.analysisComplete = false;
  }
},
    
    checkAndShowFileSizeWarning(fileSize) {
      if (fileSize > LARGE_SIZE) {
        this.showWarningMsg(`Large file (${this.formatFileSize(fileSize)}) detected. Analysis may take several minutes.`);
      } else if (fileSize > WARNING_SIZE) {
        this.showWarningMsg(`Medium file (${this.formatFileSize(fileSize)}) detected. Analysis may take a moment.`);
      }
    },
    
    resetFileInputs() {
      if (this.$refs.file) this.$refs.file.value = '';
      if (this.$refs.folder) this.$refs.folder.value = '';
    },
    
    cancelUpload() {
      this.selectedFile = null;
      this.selectedFolder = null;
      this.analysisComplete = false;
      this.resetFileInputs();
    },
    
    selectAll() {
      this.selectedTypes = this.fileTypes.map(type => type.value);
    },
    
    deselectAll() {
      this.selectedTypes = [];
    },
    
    async analyzeCode() {
      if (!this.hasSelectedFile || this.selectedTypes.length === 0) {
        this.showErrorMsg("Please select files and file types first");
        return;
      }
      
      this.isProcessing = true;
      this.progress = 0;
      this.currentFile = '';
      this.processedFiles = 0;
      this.totalFiles = 0;
      this.analysisComplete = false;
      this.totalLines = 0;
      this.totalFilesProcessed = 0;
      this.fileTypeSummary = [];
      this.largestFiles = [];
      this.allFiles = [];
      this.processingTime = 0;
      this.startTime = Date.now();
      
      // Reset all feature data
      this.projectTree = null;
      this.openNodes = [];
      this.searchQuery = '';
      this.complexityMetrics = { avgComplexity: 0, maxComplexity: 0, totalFunctions: 0, totalClasses: 0 };
      this.complexFiles = [];
      this.languageComplexity = [];
      this.duplicates = [];
      this.duplicateFiles = 0;
      this.languageStats = [];
      this.topImports = {};
      this.todoComments = [];
      this.dependencyGraph = {};
      this.dependencyTree = [];
      this.circularDependencies = [];
      
      try {
        if (this.selectedFileSize > 100 * 1024 * 1024) {
          this.currentFile = 'Preparing large file, please wait...';
        } else {
          this.currentFile = 'Preparing files...';
        }
        
        const files = await this.extractAndReadFiles();
        
        if (files.length === 0) {
          throw new Error('No valid files found to analyze. Please ensure you have selected source code files (.js, .py, .java, etc.)');
        }
        
        const hasMatchingFiles = files.some(file => {
          const extension = this.getFileExtension(file.name);
          return this.selectedTypes.some(type => 
            extension === type || file.name.includes(type)
          );
        });
        
        if (!hasMatchingFiles) {
          throw new Error(`No files match the selected file types (${this.selectedTypes.join(', ')}). Please select different file types or files with matching extensions.`);
        }
        
        this.createWorker();
                
        const workerData = {
          files: files,
          selectedTypes: this.selectedTypes,
          options: {
            countEmptyLines: this.countEmptyLines,
            ignoreComments: this.ignoreComments,
            analyzeDependencies: this.thoroughAnalysis ? this.analyzeDependencies : false,
            analyzeDocumentation: this.thoroughAnalysis ? this.analyzeDocumentation : false,
            thoroughAnalysis: this.thoroughAnalysis // Pass the master switch
          }
        };
        
        this.worker.postMessage({
          type: 'analyze',
          data: workerData
        });
        
      } catch (error) {
        console.error('Error in analysis:', error);
        this.showErrorMsg('Failed to analyze files: ' + error.message);
        this.isProcessing = false;
        this.cleanupWorker();
        this.cleanupMemory();
      }
    },
    
    async processFolderWithTraversal(folderItems, filesArray) {
      const items = Array.from(folderItems);
      const entries = [];
      
      for (const item of items) {
        if (item.webkitGetAsEntry) {
          const entry = item.webkitGetAsEntry();
          if (entry) {
            entries.push(entry);
          }
        }
      }
      
      if (entries.length > 0) {
        for (const entry of entries) {
          await this.traverseDirectoryEntry(entry, '', filesArray);
        }
      } else {
        await this.processFolder(items, filesArray);
      }
      
      return filesArray;
    },
    
    async traverseDirectoryEntry(entry, path, filesArray) {
      return new Promise((resolve, reject) => {
        if (entry.isFile) {
          entry.file(file => {
            this.readAndAddFile(file, path, filesArray)
              .then(resolve)
              .catch(reject);
          }, reject);
        } else if (entry.isDirectory) {
          const dirReader = entry.createReader();
          dirReader.readEntries(async (entries) => {
            for (const subEntry of entries) {
              await this.traverseDirectoryEntry(subEntry, path + entry.name + '/', filesArray);
            }
            resolve();
          }, reject);
        }
      });
    },
    
    async readAndAddFile(file, path, filesArray) {
      try {
        const content = await this.readFileAsText(file);
        
        if (content.includes('\u0000')) {
          return;
        }
        
        filesArray.push({
          name: path + file.name,
          content: content,
          size: file.size,
          type: file.type
        });
      } catch (e) {
        console.warn(`Failed to read ${file.name}:`, e.message);
      }
    },
    
    async extractAndReadFiles() {
      const files = [];
      
      if (this.selectedFile) {
        if (this.selectedFile.name.match(/\.(zip|7z|tar\.gz)$/i)) {
          await this.extractZipFile(this.selectedFile, files);
        } else {
          const content = await this.readFileAsText(this.selectedFile);
          files.push({
            name: this.selectedFile.name,
            content: content,
            size: this.selectedFile.size,
            type: this.selectedFile.type
          });
        }
      } else if (this.selectedFolder && this.selectedFolder.length > 0) {
        await this.processFolderWithTraversal(this.selectedFolder, files);
      }
      
      this.totalFiles = files.length;
      return files;
    },
    
async extractZipFile(zipFile, filesArray) {
  try {
    const zip = new JSZip();
    this.currentFile = 'Loading zip file...';
    const zipData = await zip.loadAsync(zipFile);
    
    const entries = Object.keys(zipData.files);
    
    // USE SECURITY_CONFIG here
    if (entries.length > SECURITY_CONFIG.MAX_FILES_IN_ZIP) {
      throw new Error(`Archive contains too many files (${entries.length} > ${SECURITY_CONFIG.MAX_FILES_IN_ZIP})`);
    }
    
    let extractedCount = 0;
    let totalSizeExtracted = 0;
    
    for (let i = 0; i < entries.length; i++) {
      const filename = entries[i];
      const zipEntry = zipData.files[filename];
      
      if (!zipEntry.dir) {
        if (isSuspiciousFilename(filename)) {
          this.showWarningMsg(`Skipping suspicious file: ${filename}`);
          continue;
        }
        
        // USE SECURITY_CONFIG here
        const estimatedSize = zipEntry._data?.uncompressedSize || 0;
        if (estimatedSize > SECURITY_CONFIG.MAX_FILE_SIZE_INDIVIDUAL) {
          this.showWarningMsg(`Skipping ${filename}: too large`);
          continue;
        }
        
        try {
          const content = await zipEntry.async('text');
          const fileSize = content.length;
          
          // USE SECURITY_CONFIG here
          const compressed = zipEntry._data?.compressedSize || 1;
          const compressionRatio = fileSize / compressed;
          if (compressionRatio > SECURITY_CONFIG.MAX_COMPRESSION_RATIO) {
            throw new Error(`Zip bomb detected: compression ratio ${compressionRatio.toFixed(1)}x`);
          }
          
          // USE SECURITY_CONFIG here
          totalSizeExtracted += fileSize;
          if (totalSizeExtracted > SECURITY_CONFIG.MAX_EXTRACTED_SIZE) {
            throw new Error(`Extracted size exceeds limit`);
          }
          
          const safeFilename = sanitizeFilename(filename);
          
          filesArray.push({
            name: safeFilename,
            content: content,
            size: fileSize
          });
          extractedCount++;
          
          if (i % 10 === 0) {
            this.currentFile = `Extracting: ${extractedCount} files`;
            this.progress = Math.min(50, Math.round((totalSizeExtracted / zipFile.size) * 50));
            await this.$nextTick();
          }
        } catch (e) {
          console.warn(`Skipping ${filename}: ${e.message}`);
        }
      }
    }
    
    this.currentFile = `Extracted ${extractedCount} files`;
    this.progress = 50;
    
  } catch (error) {
    throw new Error('Failed to extract zip file: ' + error.message);
  }
},
    
    async processFolder(folderItems, filesArray) {
      const items = Array.from(folderItems).filter(item => item instanceof File);
      
      if (items.length === 0) {
        throw new Error('No valid files found. Please ensure you are selecting actual files, not directories.');
      }
      
      let processedCount = 0;
      
      for (const file of items) {
        if (file.size === 0 && file.type === "" && !file.name.includes('.')) {
          continue;
        }
        
        try {
          const content = await this.readFileAsText(file);
          
          if (content && content.trim().length > 0) {
            if (content.includes('\u0000')) {
              continue;
            }
            
            filesArray.push({
              name: file.webkitRelativePath || file.name,
              content: content,
              size: file.size,
              type: file.type
            });
            processedCount++;
            
            if (processedCount % 5 === 0) {
              this.currentFile = `Read ${processedCount} files...`;
              await this.$nextTick();
            }
          }
        } catch (e) {
          console.warn(`Failed to read ${file.name}:`, e.message);
        }
      }
      
      if (processedCount === 0) {
        throw new Error(`Could not read any text files. The selected items may be directories or binary files.`);
      }
      
      this.currentFile = `Processed ${processedCount} files`;
      this.progress = 50;
      
      return filesArray;
    },
    
    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });
    },
    
    createWorker() {
      this.worker = createCodeAnalyzerWorker();
      
      this.worker.onmessage = (event) => {
        const { type, progress, currentFile, processedFiles, totalFiles, results, error } = event.data;
        
        switch (type) {
          case 'progress':
            this.progress = 50 + (progress / 2);
            if (currentFile !== undefined) this.currentFile = currentFile;
            if (processedFiles !== undefined) this.processedFiles = processedFiles;
            if (totalFiles !== undefined) this.totalFiles = totalFiles;
            break;
            
          case 'complete':
            if (this.startTime) {
              this.processingTime = ((Date.now() - this.startTime) / 1000).toFixed(2);
            }
            
            this.processResults(results);
            this.analysisComplete = true;
            this.isProcessing = false;
            this.showSuccessMsg(`Analysis completed in ${this.processingTime} seconds!`);
            this.cleanupWorker();
            break;
            
          case 'error':
            this.showErrorMsg(error || 'Worker encountered an error');
            this.isProcessing = false;
            this.cleanupWorker();
            this.cleanupMemory();
            break;
        }
      };
      
      this.worker.onerror = (error) => {
        console.error('Worker error:', error);
        this.showErrorMsg('Worker failed to execute');
        this.isProcessing = false;
        this.cleanupWorker();
        this.cleanupMemory();
      };
    },
    
    cleanupWorker() {
      if (this.worker) {
        this.worker.terminate();
        this.worker = null;
      }
    },
    
    cleanupMemory() {
      this.allFiles = [];
      this.largestFiles = [];
      this.fileTypeSummary = [];
      
      if (typeof window.gc === 'function') {
        window.gc();
      }
    },
    
// Feature #1 - Initialize charts
initializeCharts() {
  // Ensure refs exist before creating charts
  if (!this.$refs.pieChart || !this.$refs.barChart) {
    console.log('Chart refs not ready yet');
    return;
  }
  
  // Destroy existing charts to prevent memory leaks
  this.destroyCharts();
  
  // Create new charts with a small delay to ensure canvas is ready
  this.$nextTick(() => {
    this.createPieChart();
    this.createBarChart();
    this.createComplexityChart();
    this.createLanguageChart();
    this.createLineChart();
  });
},

destroyCharts() {
  if (this.pieChart) {
    this.pieChart.destroy();
    this.pieChart = null;
  }
  if (this.barChart) {
    this.barChart.destroy();
    this.barChart = null;
  }
  if (this.complexityChart) {
    this.complexityChart.destroy();
    this.complexityChart = null;
  }
  if (this.languageChart) {
    this.languageChart.destroy();
    this.languageChart = null;
  }
  if (this.lineChart) {
    this.lineChart.destroy();
    this.lineChart = null;
  }
},
    
createPieChart() {
  if (!this.$refs.pieChart) {
    console.log('Pie chart ref not found');
    return;
  }
  
  const ctx = this.$refs.pieChart.getContext('2d');
  if (!ctx) return;
  
  if (this.pieChart) {
    this.pieChart.destroy();
  }
  
  const labels = this.fileTypeSummary.map(item => this.getFileTypeLabel(item.type));
  const data = this.fileTypeSummary.map(item => item.lines);
  
  // Only create chart if we have data
  if (data.length === 0) return;
  
  const colors = this.generateColors(data.length);
  
  try {
    this.pieChart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: colors,
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            labels: { boxWidth: 12, font: { size: 10 } }
          }
        }
      }
    });
    console.log('Pie chart created successfully');
  } catch (error) {
    console.error('Error creating pie chart:', error);
  }
},

createBarChart() {
  if (!this.$refs.barChart) {
    console.log('Bar chart ref not found');
    return;
  }
  
  const ctx = this.$refs.barChart.getContext('2d');
  if (!ctx) return;
  
  if (this.barChart) {
    this.barChart.destroy();
  }
  
  const ranges = this.sizeRanges;
  const data = ranges.map(r => r.count);
  
  if (data.every(val => val === 0)) return;
  
  try {
    this.barChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ranges.map(r => r.label),
        datasets: [{
          label: 'Number of Files',
          data: data,
          backgroundColor: ranges.map(r => this.getColorFromName(r.color)),
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            ticks: { stepSize: 1 }
          }
        }
      }
    });
    console.log('Bar chart created successfully');
  } catch (error) {
    console.error('Error creating bar chart:', error);
  }
},

createComplexityChart() {
  if (!this.$refs.complexityChart) {
    console.log('Complexity chart ref not found');
    return;
  }
  
  if (this.complexFiles.length === 0) return;
  
  const ctx = this.$refs.complexityChart.getContext('2d');
  if (!ctx) return;
  
  if (this.complexityChart) {
    this.complexityChart.destroy();
  }
  
  const topFiles = this.complexFiles.slice(0, 10);
  const data = topFiles.map(f => f.complexity);
  
  if (data.every(val => val === 0)) return;
  
  try {
    this.complexityChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: topFiles.map(f => {
          const name = f.name.split('/').pop();
          return name.length > 15 ? name.substring(0, 12) + '...' : name;
        }),
        datasets: [{
          label: 'Cyclomatic Complexity',
          data: data,
          backgroundColor: topFiles.map(f => this.getComplexityColor(f.complexity, true)),
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: { beginAtZero: true }
        },
        plugins: {
          legend: { display: false }
        }
      }
    });
    console.log('Complexity chart created successfully');
  } catch (error) {
    console.error('Error creating complexity chart:', error);
  }
},

createLanguageChart() {
  if (!this.$refs.languageChart) {
    console.log('Language chart ref not found');
    return;
  }
  
  if (this.languageStats.length === 0) return;
  
  const ctx = this.$refs.languageChart.getContext('2d');
  if (!ctx) return;
  
  if (this.languageChart) {
    this.languageChart.destroy();
  }
  
  const data = this.languageStats.map(l => l.files);
  if (data.every(val => val === 0)) return;
  
  try {
    this.languageChart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: this.languageStats.map(l => l.language),
        datasets: [{
          data: data,
          backgroundColor: this.generateColors(this.languageStats.length),
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            labels: { boxWidth: 12, font: { size: 10 } }
          }
        }
      }
    });
    console.log('Language chart created successfully');
  } catch (error) {
    console.error('Error creating language chart:', error);
  }
},

createLineChart() {
  if (!this.$refs.lineChart) {
    console.log('Line chart ref not found');
    return;
  }
  
  if (this.allFiles.length === 0) return;
  
  const ctx = this.$refs.lineChart.getContext('2d');
  if (!ctx) return;
  
  if (this.lineChart) {
    this.lineChart.destroy();
  }
  
  try {
    const sortedFiles = [...this.allFiles].sort((a, b) => a.size - b.size);
    const chunks = this.chunkArray(sortedFiles, Math.max(1, Math.floor(sortedFiles.length / 10)));
    
    this.lineChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: chunks.map((_, i) => `Group ${i + 1}`),
        datasets: [{
          label: 'Total Lines',
          data: chunks.map(chunk => chunk.reduce((sum, f) => sum + (f.lines || 0), 0)),
          borderColor: '#1976d2',
          backgroundColor: 'rgba(25, 118, 210, 0.1)',
          tension: 0.4,
          fill: true
        }, {
          label: 'File Count',
          data: chunks.map(chunk => chunk.length),
          borderColor: '#4caf50',
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
          tension: 0.4,
          fill: true,
          yAxisID: 'y1'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: { display: true, text: 'Lines' }
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: { display: true, text: 'Files' },
            grid: { drawOnChartArea: false }
          }
        }
      }
    });
    console.log('Line chart created successfully');
  } catch (error) {
    console.error('Error creating line chart:', error);
  }
},
    // Feature #4 - Duplicate detection
    detectDuplicates(files) {
      const hashes = new Map();
      const duplicates = [];
      const minLines = 5;
      
      files.forEach(file => {
        // Guard against undefined content
        if (!file || !file.content || file.lines < minLines) return;
        
        const normalized = this.normalizeCode(file.content);
        const hash = this.hashCode(normalized);
        
        if (hashes.has(hash)) {
          const original = hashes.get(hash);
          const dupGroup = duplicates.find(d => d.original === original.name);
          
          if (dupGroup) {
            dupGroup.duplicates.push(file.name);
            dupGroup.lines = file.lines;
          } else {
            duplicates.push({
              original: original.name,
              duplicates: [file.name],
              similarity: '100%',
              lines: file.lines,
              code: file.content.substring(0, 500)
            });
          }
        } else {
          hashes.set(hash, file);
        }
      });
      
      const validDuplicates = duplicates.filter(d => d.duplicates.length > 0);
      
      const duplicateFilesSet = new Set();
      validDuplicates.forEach(dup => {
        duplicateFilesSet.add(dup.original);
        dup.duplicates.forEach(f => duplicateFilesSet.add(f));
      });
      
      return {
        duplicates: validDuplicates,
        duplicateFiles: duplicateFilesSet.size
      };
    },
    
    normalizeCode(code) {
      // Guard against undefined code
      if (!code) return '';
      
      return code
        .replace(/\s+/g, ' ')
        .replace(/\/\/.*|\/\*[\s\S]*?\*\//g, '')
        .replace(/\b\d+\b/g, '0')
        .replace(/\b[a-zA-Z_]\w*\b/g, match => {
          const keywords = ['if', 'else', 'for', 'while', 'function', 'return', 'class'];
          return keywords.includes(match) ? match : 'var';
        })
        .trim();
    },
    
    hashCode(str) {
      // Guard against undefined string
      if (!str) return '0';
      
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      return hash.toString(36);
    },
    
    // Feature #5 - Language analysis
    // Also update analyzeLanguageFeatures to ensure functions/classes are counted
    analyzeLanguageFeatures(files) {
      const languageStats = {};
      const topImports = {};
      const todoComments = [];
      
      files.forEach(file => {
        const ext = this.getFileExtension(file.name);
        const language = this.getLanguageFromExt(ext);
        
        if (!languageStats[language]) {
          languageStats[language] = {
            language,
            files: 0,
            lines: 0,
            functions: 0,
            classes: 0,
            imports: 0,
            exports: 0,
            todos: 0,
            fixmes: 0
          };
        }
        
        const stats = languageStats[language];
        stats.files++;
        stats.lines += file.lines || 0;
        
        // Only process if content exists
        if (file.content) {
          const imports = this.extractImports(file.content, ext);
          stats.imports += imports.length;
          
          if (!topImports[language]) topImports[language] = {};
          imports.forEach(imp => {
            topImports[language][imp] = (topImports[language][imp] || 0) + 1;
          });
          
          // FIX: Count functions properly
          const funcMatches = file.content.match(this.getFunctionRegex(ext)) || [];
          stats.functions += funcMatches.length;
          
          // Also count arrow functions for JS/TS
          if (ext === '.js' || ext === '.ts') {
            const arrowMatches = file.content.match(/\bconst\s+\w+\s*=\s*\([^)]*\)\s*=>/g) || [];
            stats.functions += arrowMatches.length;
          }
          
          const classMatches = file.content.match(this.getClassRegex(ext)) || [];
          stats.classes += classMatches.length;
          
          const lines = file.content.split('\n');
          lines.forEach((line, index) => {
            const todoMatch = line.match(/\/\/\s*(TODO|FIXME|HACK):?\s*(.+)/i);
            if (todoMatch) {
              todoComments.push({
                type: todoMatch[1].toUpperCase(),
                comment: todoMatch[2].trim(),
                file: file.name,
                line: index + 1
              });
            }
          });
        }
      });
      
      const statsArray = Object.values(languageStats).sort((a, b) => b.lines - a.lines);
      
      const sortedImports = {};
      Object.keys(topImports).forEach(lang => {
        sortedImports[lang] = Object.entries(topImports[lang])
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);
      });
      
      return {
        languageStats: statsArray,
        topImports: sortedImports,
        todoComments: todoComments.sort((a, b) => a.file.localeCompare(b.file))
      };
    },
    
    extractImports(code, ext) {
      // Guard against undefined code
      if (!code) return [];
      
      const imports = [];
      
      switch(ext) {
        case '.js':
        case '.ts': {
          const es6Matches = code.match(/import\s+.*\s+from\s+['"]([^'"]+)['"]/g) || [];
          es6Matches.forEach(m => {
            const match = m.match(/from\s+['"]([^'"]+)['"]/);
            if (match) imports.push(match[1]);
          });
          
          const requireMatches = code.match(/require\s*\(\s*['"]([^'"]+)['"]\s*\)/g) || [];
          requireMatches.forEach(m => {
            const match = m.match(/['"]([^'"]+)['"]/);
            if (match) imports.push(match[1]);
          });
          break;
        }
          
        case '.py': {
          const pyMatches = code.match(/^(?:from\s+(\S+)\s+)?import\s+(\S+)/gm) || [];
          pyMatches.forEach(m => {
            if (m.startsWith('from')) {
              const match = m.match(/from\s+(\S+)\s+import/);
              if (match) imports.push(match[1]);
            } else {
              const match = m.match(/import\s+(\S+)/);
              if (match) imports.push(match[1]);
            }
          });
          break;
        }
      }
      
      return imports;
    },
    
    getFunctionRegex(ext) {
      switch(ext) {
        case '.js':
        case '.ts':
          return /\bfunction\s+\w+|\bconst\s+\w+\s*=\s*\(|\b\w+\s*:\s*function/g;
        case '.py':
          return /\bdef\s+\w+/g;
        case '.java':
          return /(public|private|protected)?\s+\w+\s+\w+\s*\([^)]*\)\s*{/g;
        default:
          return /$^/;
      }
    },
    
    getClassRegex(ext) {
      switch(ext) {
        case '.js':
        case '.ts':
          return /\bclass\s+\w+/g;
        case '.py':
          return /\bclass\s+\w+/g;
        case '.java':
          return /(public|private)?\s+class\s+\w+/g;
        default:
          return /$^/;
      }
    },
    
    // Feature #3 - Search methods
    treeFilter(item, search, textKey) {
      if (!search) return true;
      
      const searchLower = search.toLowerCase();
      
      if (item.name.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      if (item.children && item.children.length > 0) {
        return item.children.some(child => this.treeFilter(child, search, textKey));
      }
      
      return false;
    },
    
    isSearchMatch(item) {
      if (!this.searchQuery) return false;
      return item.name.toLowerCase().includes(this.searchQuery.toLowerCase());
    },
    
    getMatchingNodes(tree) {
      const nodes = [];
      
      const traverse = (items) => {
        items.forEach(item => {
          if (item.children) {
            const hasMatch = item.children.some(child => 
              child.name.toLowerCase().includes(this.searchQuery.toLowerCase())
            );
            if (hasMatch) {
              nodes.push(item.id);
              traverse(item.children);
            }
          }
        });
      };
      
      traverse(tree);
      return nodes;
    },
    
    filterTree(items) {
      if (!this.searchQuery) return items;
      
      return items.filter(item => {
        if (item.type === 'folder') {
          const filteredChildren = this.filterTree(item.children || []);
          item.children = filteredChildren;
          return filteredChildren.length > 0 || 
                 item.name.toLowerCase().includes(this.searchQuery.toLowerCase());
        }
        return item.name.toLowerCase().includes(this.searchQuery.toLowerCase());
      });
    },
    
    // Main processResults with all features
processResults(results) {
  this.resetPagination();
  console.log('Processing results:', results);
  
  // Sanitize ALL filenames and content
  // Store full content for search, truncated for display
  const sanitizedFiles = (results.allFiles || []).map(file => ({
    ...file,
    name: sanitizeFilename(file.name),
    // Keep full content for search
    content: file.content || '',
    // Create a truncated version for display if needed
    displayContent: file.content ? file.content.substring(0, 1000) : '',
    safeName: sanitizeForDisplay(file.name),
    docQuality: file.docQuality || { commentRatio: 0, totalDocs: 0 }
  }));
  
  this.totalLines = results.totalLines || 0;
  this.totalFilesProcessed = results.totalFilesProcessed || 0;
  
  // Sanitize file type summary
  this.fileTypeSummary = (results.fileTypeSummary || []).map(item => ({
    ...item,
    type: sanitizeFilename(item.type),
    percentage: this.totalLines > 0 ? ((item.lines / this.totalLines) * 100).toFixed(1) : 0
  }));
  
  this.allFiles = sanitizedFiles;
  this.largestFiles = sanitizedFiles.slice(0, 20);
  
  // Sanitize language stats
  this.languageStats = (results.languageStats || []).map(stat => ({
    ...stat,
    language: sanitizeForDisplay(stat.language)
  }));
  
  // Build project tree with sanitized names
  if (this.allFiles && this.allFiles.length > 0) {
    this.projectTree = this.buildProjectTree(this.allFiles);
    this.openNodes = this.projectTree
      .filter(item => item.type === 'folder')
      .map(item => item.id);
  }
  
  // Calculate complexity
this.complexityMetrics = results.complexityMetrics || { avgComplexity: 0, maxComplexity: 0, totalFunctions: 0, totalClasses: 0 };
this.complexFiles = (results.complexFiles || []).map(file => ({
  ...file,
  name: sanitizeFilename(file.name),
}));
this.languageComplexity = results.languageComplexity || [];
  
  // Duplicates - sanitize names and truncate code
  const duplicateResults = this.detectDuplicates(this.allFiles);
  this.duplicates = duplicateResults.duplicates.map(dup => ({
    ...dup,
    original: sanitizeFilename(dup.original),
    duplicates: dup.duplicates.map(f => sanitizeFilename(f)),
    code: dup.code ? dup.code.substring(0, 500) : ''
  }));
  this.duplicateFiles = duplicateResults.duplicateFiles;
  
    // Add error summary
  this.errorSummary = results.errorSummary || null;
  
  // Make sure files have errors/warnings arrays
  this.allFiles = sanitizedFiles.map(file => ({
    ...file,
    errors: file.errors || [],
    warnings: file.warnings || []
  }));

  
  this.analyzeCodeChurn(this.allFiles);
  this.calculateTechDebt();

  // Language features
  if (!results.languageFeatures) {
    const languageResults = this.analyzeLanguageFeatures(this.allFiles);
    this.topImports = languageResults.topImports;
    this.todoComments = languageResults.todoComments.map(todo => ({
      ...todo,
      file: sanitizeFilename(todo.file),
      comment: sanitizeForDisplay(todo.comment)
    }));
  } else {
    this.topImports = results.languageFeatures.topImports || {};
    this.todoComments = (results.languageFeatures.todoComments || []).map(todo => ({
      ...todo,
      file: sanitizeFilename(todo.file),
      comment: sanitizeForDisplay(todo.comment)
    }));
  }

  
  
  // NEW: Get dependency data from worker results
  if (results.dependencyGraph) {
    this.dependencyGraph = results.dependencyGraph;
    this.dependencyTree = results.dependencyTree || [];
    this.circularDependencies = results.circularDependencies || [];
    console.log('Dependency data received from worker');
  } else {
    console.log('No dependency data in worker results');
    this.dependencyGraph = {};
    this.dependencyTree = [];
    this.circularDependencies = [];
  }
},

      calculateLanguageStats(files) {
      const stats = {};
      
      files.forEach(file => {
        const ext = this.getFileExtension(file.name);
        const language = this.getLanguageFromExt(ext);
        
        if (!stats[language]) {
          stats[language] = {
            language,
            files: 0,
            lines: 0,
            functions: 0,
            classes: 0
          };
        }
        
        stats[language].files++;
        stats[language].lines += file.lines || 0;
        
        // Count functions and classes
        if (file.content) {
          const functions = this.countFunctions(file.content, ext);
          const classes = this.countClasses(file.content, ext);
          stats[language].functions += functions;
          stats[language].classes += classes;
        }
      });
      
      return Object.values(stats).sort((a, b) => b.lines - a.lines);
    },
    
    // ADD helper methods for counting
    countFunctions(code, ext) {
      if (!code) return 0;
      
      switch(ext) {
        case '.js':
        case '.ts':
          return (code.match(/\bfunction\s+\w+/g) || []).length +
                 (code.match(/\bconst\s+\w+\s*=\s*\(/g) || []).length +
                 (code.match(/\b\w+\s*:\s*function/g) || []).length;
        case '.py':
          return (code.match(/\bdef\s+\w+/g) || []).length;
        case '.java':
          return (code.match(/(public|private|protected)?\s+\w+\s+\w+\s*\([^)]*\)\s*{/g) || []).length;
        default:
          return 0;
      }
    },
    
    countClasses(code, ext) {
      if (!code) return 0;
      
      switch(ext) {
        case '.js':
        case '.ts':
        case '.py':
        case '.java':
          return (code.match(/\bclass\s+\w+/g) || []).length;
        default:
          return 0;
      }
    },
    
    // Build project tree
    buildProjectTree(files) {
      const root = [];
      const map = {};
      
      const sortedFiles = [...files].sort((a, b) => a.name.localeCompare(b.name));
      
      sortedFiles.forEach((file, index) => {
        const parts = file.name.split('/').filter(p => p);
        let currentLevel = root;
        let currentPath = '';
        
        parts.forEach((part, i) => {
          currentPath = currentPath ? `${currentPath}/${part}` : part;
          
          if (i === parts.length - 1) {
            const fileNode = {
              id: `file-${index}-${currentPath}`,
              name: part,
              type: 'file',
              extension: this.getFileExtension(part),
              lines: file.lines || 0,
              size: file.size || 0,
              path: file.name,
              complexity: file.complexity || 0
            };
            currentLevel.push(fileNode);
          } else {
            let folder = currentLevel.find(item => item.type === 'folder' && item.name === part);
            
            if (!folder) {
              folder = {
                id: `folder-${currentPath}`,
                name: part,
                type: 'folder',
                children: [],
                fileCount: 0,
                path: currentPath
              };
              currentLevel.push(folder);
              map[currentPath] = folder;
            }
            
            folder.fileCount++;
            currentLevel = folder.children;
          }
        });
      });
      
      return root;
    },
    
    // Tree view methods
    onOpenUpdate(openNodes) {
      this.openNodes = openNodes;
    },
    
    expandAll() {
      if (!this.projectTree) return;
      
      const collectFolderIds = (items) => {
        let ids = [];
        items.forEach(item => {
          if (item.type === 'folder') {
            ids.push(item.id);
            if (item.children) {
              ids = ids.concat(collectFolderIds(item.children));
            }
          }
        });
        return ids;
      };
      
      this.openNodes = collectFolderIds(this.projectTree);
    },
    
    collapseAll() {
      this.openNodes = [];
    },
    
    countFolders(tree) {
      let count = 0;
      const countRecursive = (items) => {
        items.forEach(item => {
          if (item.type === 'folder') {
            count++;
            if (item.children) {
              countRecursive(item.children);
            }
          }
        });
      };
      countRecursive(tree);
      return count;
    },
    
    getMaxDepth(tree, currentDepth = 1) {
      let maxDepth = currentDepth;
      const depthRecursive = (items, depth) => {
        items.forEach(item => {
          if (item.type === 'folder' && item.children && item.children.length > 0) {
            maxDepth = Math.max(maxDepth, depth + 1);
            depthRecursive(item.children, depth + 1);
          }
        });
      };
      depthRecursive(tree, currentDepth);
      return maxDepth;
    },
    
    cancelAnalysis() {
      if (this.worker) {
        this.cleanupWorker();
        this.isProcessing = false;
        this.showErrorMsg('Analysis cancelled');
        this.cleanupMemory();
      }
    },
    
    // Export methods
    exportChartData() {
      const chartData = {
        fileTypes: this.fileTypeSummary,
        sizeRanges: this.sizeRanges,
        complexity: this.complexFiles.slice(0, 20),
        languages: this.languageStats
      };
      
      const dataStr = JSON.stringify(chartData, null, 2);
      this.downloadFile(dataStr, 'chart-data.json', 'application/json');
    },
    
generateHTMLReport() {
  // USE: Escape function for HTML
  const escapeForHtml = (text) => {
    return escapeHtml(text);
  };
  
  const avgComplexity = typeof this.complexityMetrics.avgComplexity === 'number' 
    ? this.complexityMetrics.avgComplexity.toFixed(1) 
    : '0';
  
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline';">
  <title>Code Analysis Report</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 40px; }
    h1 { color: #1976d2; }
    .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin: 30px 0; }
    .stat-card { background: #f5f5f5; padding: 20px; border-radius: 8px; text-align: center; }
    .stat-value { font-size: 32px; font-weight: bold; color: #1976d2; }
    .stat-label { color: #666; margin-top: 10px; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th { background: #1976d2; color: white; padding: 10px; text-align: left; }
    td { padding: 10px; border-bottom: 1px solid #ddd; }
  </style>
</head>
<body>
  <h1>Code Analysis Report</h1>
  <p>Generated on ${escapeForHtml(new Date().toLocaleString())}</p>
  
  <div class="summary">
    <div class="stat-card">
      <div class="stat-value">${escapeForHtml(this.totalLines.toLocaleString())}</div>
      <div class="stat-label">Total Lines</div>
    </div>
    <div class="stat-card">
      <div class="stat-value">${escapeForHtml(this.totalFilesProcessed)}</div>
      <div class="stat-label">Files Analyzed</div>
    </div>
    <div class="stat-card">
      <div class="stat-value">${escapeForHtml(avgComplexity)}</div>
      <div class="stat-label">Avg Complexity</div>
    </div>
    <div class="stat-card">
      <div class="stat-value">${escapeForHtml(this.duplicates.length)}</div>
      <div class="stat-label">Duplicates Found</div>
    </div>
  </div>
  
  <h2>File Types</h2>
  <table>
    <tr>
      <th>Type</th>
      <th>Files</th>
      <th>Lines</th>
      <th>Percentage</th>
    </tr>
    ${this.fileTypeSummary.map(item => `
      <tr>
        <td>${escapeForHtml(this.getFileTypeLabel(item.type))}</td>
        <td>${escapeForHtml(item.count)}</td>
        <td>${escapeForHtml(item.lines.toLocaleString())}</td>
        <td>${escapeForHtml(item.percentage)}%</td>
      </tr>
    `).join('')}
  </table>
  
  <h2>Most Complex Files</h2>
  <table>
    <tr>
      <th>File</th>
      <th>Complexity</th>
      <th>Nesting</th>
      <th>Functions</th>
      <th>Lines</th>
    </tr>
    ${this.complexFiles.slice(0, 10).map(file => `
      <tr>
        <td>${escapeForHtml(file.name)}</td>
        <td>${escapeForHtml(file.complexity)}</td>
        <td>${escapeForHtml(file.nesting)}</td>
        <td>${escapeForHtml(file.functions)}</td>
        <td>${escapeForHtml(file.lines)}</td>
      </tr>
    `).join('')}
  </table>
  
  ${this.duplicates.length > 0 ? `
    <h2>Duplicate Code Blocks</h2>
    <table>
      <tr>
        <th>Original File</th>
        <th>Duplicates</th>
        <th>Lines</th>
      </tr>
      ${this.duplicates.map(dup => `
        <tr>
          <td>${escapeForHtml(dup.original)}</td>
          <td>${dup.duplicates.map(f => escapeForHtml(f)).join('<br>')}</td>
          <td>${escapeForHtml(dup.lines)}</td>
        </tr>
      `).join('')}
    </table>
  ` : ''}
  
  ${this.circularDependencies.length > 0 ? `
    <h2>Circular Dependencies</h2>
    <ul>
      ${this.circularDependencies.map(circle => `
        <li>${circle.map(f => escapeForHtml(f)).join(' → ')}</li>
      `).join('')}
    </ul>
  ` : ''}
</body>
</html>`;
  
  this.downloadFile(html, 'code-analysis-report.html', 'text/html');
},
    
showDuplicateCode(dup) {
  // ADD: Sanitize code before displaying
  this.selectedCode = sanitizeCode(dup.code || '');
  this.showCodeDialog = true;
},
    
    showDependents(fileName) {
      const dependents = this.dependencyGraph[fileName]?.dependents || [];
      if (dependents.length > 0) {
        this.showInfoMsg(`"${fileName}" is used by: ${dependents.join(', ')}`);
      } else {
        this.showInfoMsg(`"${fileName}" has no dependents`);
      }
    },
    
    analyzeCircular(fileName) {
      const cycles = this.circularDependencies.filter(cycle => cycle.includes(fileName));
      if (cycles.length > 0) {
        this.showWarningMsg(`Circular dependencies found: ${cycles.map(c => c.join(' → ')).join('; ')}`);
      } else {
        this.showSuccessMsg(`No circular dependencies involving "${fileName}"`);
      }
    },
    
    hasDependency(file, dependency) {
      return this.dependencyGraph[file] && 
             this.dependencyGraph[file].dependencies.includes(dependency);
    },
    
    // Utility methods
    getFileIcon(extension) {
      const iconMap = {
        '.js': 'mdi-language-javascript',
        '.ts': 'mdi-language-typescript',
        '.py': 'mdi-language-python',
        '.java': 'mdi-language-java',
        '.html': 'mdi-language-html5',
        '.css': 'mdi-language-css3',
        '.scss': 'mdi-sass',
        '.vue': 'mdi-vuejs',
        '.json': 'mdi-code-json',
        '.md': 'mdi-language-markdown',
        '.xml': 'mdi-xml',
        '.yaml': 'mdi-format-list-bulleted',
        '.yml': 'mdi-format-list-bulleted',
        '.sh': 'mdi-bash',
        '.txt': 'mdi-file-document-outline',
        'Dockerfile': 'mdi-docker',
        'Makefile': 'mdi-gnu',
        '.gitignore': 'mdi-git'
      };
      return iconMap[extension] || 'mdi-file-code-outline';
    },
    
    getFileIconColor(extension) {
      const colorMap = {
        '.js': 'amber darken-3',
        '.ts': 'blue darken-3',
        '.py': 'blue darken-3',
        '.java': 'red darken-3',
        '.html': 'orange darken-3',
        '.css': 'blue lighten-1',
        '.scss': 'pink lighten-2',
        '.vue': 'green darken-2',
        '.json': 'grey darken-1',
        '.md': 'grey',
        '.xml': 'green lighten-2',
        '.yaml': 'cyan darken-2',
        '.yml': 'cyan darken-2',
        '.sh': 'grey darken-3',
        '.txt': 'grey lighten-1'
      };
      return colorMap[extension] || 'grey';
    },
    
    getLineCountColor(lines) {
      if (lines < 50) return 'success';
      if (lines < 200) return 'info';
      if (lines < 500) return 'warning';
      return 'error';
    },
    
    getLanguageFromExt(ext) {
      const languageMap = {
        '.js': 'JavaScript',
        '.ts': 'TypeScript',
        '.py': 'Python',
        '.java': 'Java',
        '.html': 'HTML',
        '.css': 'CSS',
        '.scss': 'SCSS',
        '.vue': 'Vue',
        '.json': 'JSON',
        '.md': 'Markdown',
        '.xml': 'XML',
        '.yaml': 'YAML',
        '.yml': 'YAML',
        '.sh': 'Shell',
        '.c': 'C',
        '.cpp': 'C++',
        '.cs': 'C#',
        '.go': 'Go',
        '.php': 'PHP',
        '.rb': 'Ruby',
        'Dockerfile': 'Docker',
        'Makefile': 'Make',
        '.txt': 'Text'
      };
      return languageMap[ext] || 'Other';
    },
    
    getLanguageIcon(language) {
      const iconMap = {
        'JavaScript': 'mdi-language-javascript',
        'TypeScript': 'mdi-language-typescript',
        'Python': 'mdi-language-python',
        'Java': 'mdi-language-java',
        'HTML': 'mdi-language-html5',
        'CSS': 'mdi-language-css3',
        'Vue': 'mdi-vuejs',
        'JSON': 'mdi-code-json',
        'Markdown': 'mdi-language-markdown',
        'XML': 'mdi-xml',
        'YAML': 'mdi-format-list-bulleted',
        'Shell': 'mdi-bash',
        'Docker': 'mdi-docker',
        'Make': 'mdi-gnu'
      };
      return iconMap[language] || 'mdi-code-tags';
    },
    
    getLanguageColor(language) {
      const colorMap = {
        'JavaScript': 'amber darken-3',
        'TypeScript': 'blue darken-3',
        'Python': 'blue darken-3',
        'Java': 'red darken-3',
        'HTML': 'orange darken-3',
        'CSS': 'blue lighten-1',
        'Vue': 'green darken-2',
        'JSON': 'grey darken-1',
        'Markdown': 'grey',
        'XML': 'green lighten-2',
        'YAML': 'cyan darken-2',
        'Shell': 'grey darken-3',
        'Docker': 'blue-grey darken-2',
        'Make': 'brown darken-2'
      };
      return colorMap[language] || 'grey';
    },
    
    getComplexityColor(complexity, isBackground = false) {
      if (complexity < 5) return isBackground ? '#4caf50' : 'success';
      if (complexity < 10) return isBackground ? '#2196f3' : 'info';
      if (complexity < 20) return isBackground ? '#ff9800' : 'warning';
      return isBackground ? '#f44336' : 'error';
    },
    
    getNestingColor(nesting) {
      if (nesting < 3) return 'success';
      if (nesting < 5) return 'info';
      if (nesting < 8) return 'warning';
      return 'error';
    },
    
    getDependencyIcon(item) {
      if (item.circular) return 'mdi-alert-circle';
      if (item.missing) return 'mdi-help-circle';
      if (item.dependencies && item.dependencies.length > 0) return 'mdi-graph';
      return 'mdi-file-outline';
    },
    
    getDependencyIconColor(item) {
      if (item.circular) return 'error';
      if (item.missing) return 'warning';
      return 'primary';
    },
    
    generateColors(count) {
      const colors = [];
      const hueStep = 360 / count;
      
      for (let i = 0; i < count; i++) {
        const hue = i * hueStep;
        colors.push(`hsl(${hue}, 70%, 60%)`);
      }
      
      return colors;
    },
    
    getColorFromName(colorName) {
      const colorMap = {
        'blue': '#2196f3',
        'green': '#4caf50',
        'yellow': '#ffeb3b',
        'orange': '#ff9800',
        'red': '#f44336',
        'primary': '#1976d2',
        'success': '#4caf50',
        'info': '#2196f3',
        'warning': '#ff9800',
        'error': '#f44336'
      };
      return colorMap[colorName] || '#9e9e9e';
    },
    
    chunkArray(array, size) {
      const chunks = [];
      for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
      }
      return chunks;
    },
    
    // Export methods
    exportResults() {
      const results = {
        timestamp: new Date().toISOString(),
        summary: {
          totalLines: this.totalLines,
          totalFiles: this.totalFilesProcessed,
          fileTypes: this.fileTypeSummary.length,
          processingTime: this.processingTime,
          totalSize: this.selectedFileSize
        },
        byFileType: this.fileTypeSummary,
        largestFiles: this.largestFiles,
        projectStructure: this.projectTree,
        complexity: {
          metrics: this.complexityMetrics,
          complexFiles: this.complexFiles.slice(0, 50),
          byLanguage: this.languageComplexity
        },
        duplicates: this.duplicates,
        languageAnalysis: {
          stats: this.languageStats,
          topImports: this.topImports,
          todos: this.todoComments
        },
        dependencies: {
          graph: this.dependencyGraph,
          circular: this.circularDependencies
        },
        settings: {
          countEmptyLines: this.countEmptyLines,
          ignoreComments: this.ignoreComments,
          selectedFileTypes: this.selectedTypes
        }
      };
      
      const dataStr = JSON.stringify(results, null, 2);
      this.downloadFile(dataStr, 'code-analysis.json', 'application/json');
    },
    
    exportCSV() {
      let csv = 'File Type,Files,Lines,Percentage\n';
      
      this.fileTypeSummary.forEach(item => {
        const percentage = this.totalLines > 0 ? ((item.lines / this.totalLines) * 100).toFixed(2) : '0';
        csv += `"${this.getFileTypeLabel(item.type)}",${item.count},${item.lines},${percentage}%\n`;
      });
      
      csv += '\n\nTop Files\n';
      csv += 'Rank,File Name,Lines,Size,Complexity\n';
      
      this.largestFiles.slice(0, 10).forEach((file, index) => {
        csv += `${index + 1},"${file.name}",${file.lines},"${this.formatFileSize(file.size)}",${file.complexity || 0}\n`;
      });
      
      csv += '\n\nProject Structure\n';
      csv += 'Type,Name,Path,Size (bytes),Lines,Complexity\n';
      
      const addToCSV = (items, prefix = '') => {
        items.forEach(item => {
          if (item.type === 'folder') {
            csv += `Folder,${item.name},${item.path || prefix},,,,\n`;
            if (item.children) {
              addToCSV(item.children, item.path);
            }
          } else {
            csv += `File,${item.name},${item.path || prefix},${item.size || 0},${item.lines || 0},${item.complexity || 0}\n`;
          }
        });
      };
      
      if (this.projectTree) {
        addToCSV(this.projectTree);
      }
      
      csv += '\n\nComplex Files\n';
      csv += 'File,Complexity,Nesting Depth,Functions,Classes,Lines\n';
      this.complexFiles.slice(0, 20).forEach(file => {
        csv += `"${file.name}",${file.complexity},${file.nesting},${file.functions},${file.classes},${file.lines}\n`;
      });
      
      if (this.duplicates.length > 0) {
        csv += '\n\nDuplicates\n';
        csv += 'Original File,Duplicate Files,Lines\n';
        this.duplicates.forEach(dup => {
          csv += `"${dup.original}","${dup.duplicates.join('; ')}",${dup.lines}\n`;
        });
      }
      
      this.downloadFile(csv, 'code-analysis.csv', 'text/csv');
    },
    
    exportStructure() {
      if (!this.projectTree) {
        this.showErrorMsg('No project structure to export');
        return;
      }
      
      const structure = {
        timestamp: new Date().toISOString(),
        tree: this.projectTree,
        stats: this.structureStats,
        complexity: this.complexFiles,
        dependencies: this.circularDependencies
      };
      
      const dataStr = JSON.stringify(structure, null, 2);
      this.downloadFile(dataStr, 'project-structure.json', 'application/json');
    },
    
    downloadFile(content, filename, mimeType) {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    
startNewAnalysis() {
  this.cleanupWorker();
  this.cleanupMemory();
  this.selectedFile = null;
  this.selectedFolder = null;
  this.selectedTypes = [];
  this.analysisComplete = false;
  this.totalLines = 0;
  this.totalFilesProcessed = 0;
  this.fileTypeSummary = [];
  this.largestFiles = [];
  this.allFiles = [];
  this.processingTime = 0;
  
  // Reset all features
  this.projectTree = null;
  this.openNodes = [];
  this.searchQuery = '';
  this.complexityMetrics = { avgComplexity: 0, maxComplexity: 0, totalFunctions: 0, totalClasses: 0 };
  this.complexFiles = [];
  this.languageComplexity = [];
  this.duplicates = [];
  this.duplicateFiles = 0;
  this.languageStats = [];
  this.topImports = {};
  this.todoComments = [];
  this.dependencyGraph = {};
  this.dependencyTree = [];
  this.circularDependencies = [];
  
  // Destroy charts
  this.destroyCharts();
  
  this.resetFileInputs();
},
    // Utility methods
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },
    
    getFileTypeLabel(extension) {
      const type = this.fileTypes.find(t => t.value === extension);
      return type ? type.label.replace(/\(.*\)/, '').trim() : extension;
    },
    
    getExtensionIcon(extension) {
      if (extension === '.js') return 'JS';
      if (extension === '.ts') return 'TS';
      if (extension === '.py') return 'PY';
      if (extension === '.java') return 'JV';
      if (extension === '.vue') return 'VUE';
      if (extension === '.html') return 'HTML';
      if (extension === '.css') return 'CSS';
      if (extension === '.json') return 'JSON';
      if (extension === '.md') return 'MD';
      if (extension === '.sh') return 'SH';
      if (extension === 'Dockerfile') return 'DF';
      if (extension === 'Makefile') return 'MF';
      return extension.substring(1, 3).toUpperCase();
    },
    
    getRankColor(index) {
      if (index === 0) return 'gold darken-1';
      if (index === 1) return 'grey darken-1';
      if (index === 2) return 'orange darken-3';
      const colors = ['primary', 'secondary', 'success', 'info', 'warning'];
      return colors[index % colors.length];
    },
    
    getFileExtension(filename) {
      const lastDot = filename.lastIndexOf('.');
      if (lastDot === -1) {
        if (filename === 'Dockerfile' || filename === 'Makefile' || filename === '.gitignore') {
          return filename;
        }
        return '';
      }
      return filename.substring(lastDot).toLowerCase();
    },
    
    // Message methods
    showErrorMsg(message) {
      this.errorMessage = message;
      this.showError = true;
    },
    
    showSuccessMsg(message) {
      this.successMessage = message;
      this.showSuccess = true;
    },
    
    showWarningMsg(message) {
      this.warningMessage = message;
      this.showWarning = true;
    },
    
    showInfoMsg(message) {
      this.successMessage = message;
      this.showSuccess = true;
    },
    
  },

};
</script>

<style scoped>
/* ===== BASE STYLES ===== */
canvas {
  display: block;
  width: 100% !important;
  height: 300px !important;
  max-height: 300px;
  background-color: transparent;
}
/* New styles for health metrics */
.v-progress-circular {
  transition: all 0.3s ease;
}

.health-metric-card {
  transition: transform 0.2s ease;
}

.health-metric-card:hover {
  transform: translateY(-2px);
}

/* Search loading state */
.v-text-field--loading .v-input__slot {
  opacity: 0.8;
}

/* Recommendation list styling */
.v-list-item {
  transition: background-color 0.2s ease;
}

.v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.theme--dark .v-list-item:hover {
  background-color: rgba(255, 255, 255, 0.02);
}

/* Progress persistence indicators */
.v-card-actions {
  background-color: rgba(0, 0, 0, 0.01);
}

.theme--dark .v-card-actions {
  background-color: rgba(255, 255, 255, 0.01);
}
.v-card .pa-4 {
  position: relative;
  min-height: 350px;
}

.v-tab-item {
  min-height: 400px;
}

/* ===== DROP ZONE STYLES ===== */
.drop-zone {
  border: 2px dashed #e0e0e0;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.drop-zone-light {
  background-color: #FAFAFA;
  border-color: #e0e0e0;
}

.drop-zone-dark {
  background-color: #1E1E1E;
  border-color: #424242;
}

.drop-content {
  padding: 30px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.drop-content:hover {
  background-color: rgba(25, 118, 210, 0.04);
}

.drop-active {
  border-color: #1976d2 !important;
  background-color: rgba(25, 118, 210, 0.08) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.has-file {
  background-color: rgba(76, 175, 80, 0.04);
  border-color: #4caf50 !important;
}

.file-info {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ===== STRUCTURE TREE STYLES ===== */
.structure-tree {
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  overflow-x: hidden !important;
  word-break: break-word;
}

.v-treeview {
  padding: 8px 0;
}

.v-treeview-node__label {
  font-size: 0.9rem;
  white-space: normal !important;
  word-break: break-word;
}

.v-treeview-node__content {
  min-height: 36px;
}

.v-treeview-node__root {
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.v-treeview-node__root:hover {
  background-color: rgba(25, 118, 210, 0.04);
}

/* ===== SEARCH HIGHLIGHT ===== */
.search-highlight {
  background-color: rgba(255, 235, 59, 0.3);
  border-radius: 4px;
  padding: 2px 0;
}

/* ===== CODE PREVIEW ===== */
.code-preview {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  max-height: 500px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.5;
}

/* ===== DEPENDENCY TREE ===== */
.dependency-tree .v-treeview-node__root {
  border-left: 2px solid transparent;
}

.dependency-tree .v-treeview-node__root:hover {
  border-left-color: #1976d2;
}

.dependency-tree {
  overflow-x: hidden !important;
  max-width: 100%;
}

/* ===== CHART STYLES ===== */
.chart-container {
  position: relative;
  width: 100%;
  height: 250px;
  min-height: 200px;
}

canvas {
  max-width: 100% !important;
  height: auto !important;
  max-height: 250px;
}

/* ===== SEARCH FIELD ===== */
.search-field {
  transition: all 0.3s ease;
}

.search-field:focus-within {
  transform: scale(1.02);
}

/* ===== ANIMATIONS ===== */
.v-tab-item {
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Screenshot capture styles */
.health-metrics-section.capturing-screenshot {
  background-color: #ffffff !important;
}

.health-metrics-section.capturing-screenshot * {
  color: #000000 !important;
}

.health-metrics-section.capturing-screenshot .v-chip {
  color: #ffffff !important;
}

.health-metrics-section.capturing-screenshot .text--secondary {
  color: #666666 !important;
}

.health-metrics-section.capturing-screenshot .v-progress-circular__content {
  color: #000000 !important;
  font-weight: bold !important;
}

/* Instagram preview styles */
.instagram-preview {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 90vw;
  max-height: 90vh;
  box-shadow: 0 20px 40px rgba(0,0,0,0.3);
  border-radius: 16px;
  z-index: 9999;
}

/* Search Results Card */
.search-results-card {
  width: 100%;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  border-radius: 4px 4px 0 0;
}

.search-results-container {
  max-height: 400px;
  overflow-y: auto;
}

/* Custom scrollbar */
.search-results-container::-webkit-scrollbar {
  width: 6px;
}

.search-results-container::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

.search-results-container::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.search-results-container::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

.theme--dark .search-results-container::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.theme--dark .search-results-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

.theme--dark .search-results-container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Search Result Items */
.search-result-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: background-color 0.2s ease;
  padding: 8px 12px !important;
}

.search-result-item:hover {
  background-color: rgba(25, 118, 210, 0.04);
  cursor: pointer;
}

.search-result-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 13px;
}

.search-result-path {
  color: rgba(0, 0, 0, 0.6);
  font-size: 11px;
  display: flex;
  align-items: center;
  margin-top: 2px;
}

.match-preview {
  background-color: rgba(0, 0, 0, 0.02);
  padding: 4px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 10px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.match-preview strong {
  background-color: #ffeb3b;
  color: #000;
  padding: 0 2px;
  border-radius: 2px;
  font-weight: bold;
}

.highlight-file {
  background-color: rgba(255, 235, 59, 0.3) !important;
  transition: background-color 0.5s ease;
}

/* Loading indicator */
.scroll-trigger {
  transition: all 0.3s ease;
}

/* Dark theme overrides */
.theme--dark .search-result-item {
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

.theme--dark .search-result-item:hover {
  background-color: rgba(255, 255, 255, 0.04);
}

.theme--dark .search-result-path {
  color: rgba(255, 255, 255, 0.6);
}

.theme--dark .match-preview {
  background-color: rgba(255, 255, 255, 0.02);
  color: #aaa;
}

.theme--dark .match-preview strong {
  background-color: #f57f17;
  color: #fff;
}

/* ===== DARK MODE SUPPORT ===== */
.theme--dark .structure-tree {
  background-color: rgba(255, 255, 255, 0.02);
}

.theme--dark .code-preview {
  background-color: #2d2d2d;
  color: #e0e0e0;
}

.theme--dark .search-highlight {
  background-color: rgba(255, 235, 59, 0.2);
}

.theme--dark .v-treeview-node__root:hover {
  background-color: rgba(25, 118, 210, 0.1);
}

.theme--dark .responsive-tabs {
  scrollbar-color: #424242 #1e1e1e;
}

.theme--dark .responsive-tabs::-webkit-scrollbar {
  height: 4px;
}

.theme--dark .responsive-tabs::-webkit-scrollbar-track {
  background: #1e1e1e;
}

.theme--dark .responsive-tabs::-webkit-scrollbar-thumb {
  background: #424242;
  border-radius: 4px;
}

.theme--dark .v-data-table__wrapper {
  scrollbar-color: #424242 #1e1e1e;
}

/* ===== RESPONSIVE TABS - FIXED ===== */
.responsive-tabs {
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

.responsive-tabs .v-tab {
  min-width: auto;
  padding: 0 12px;
  flex: 0 0 auto;
}

/* ===== RESPONSIVE TABLES ===== */
.responsive-table {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 8px;
}

.responsive-table table {
  min-width: 500px;
}

/* ===== TREE LABEL STYLES ===== */
.tree-label {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
}

.tree-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-left: 4px;
}

/* ===== RANK BADGE ===== */
.rank-badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 12px;
  flex-shrink: 0;
}

/* ===== UNIVERSAL FIXES ===== */
.v-container,
.v-row,
.v-col,
.v-card,
.v-card-text,
.v-tabs-items,
.v-tab-item {
  max-width: 100% !important;
  overflow-x: hidden !important;
}

.v-data-table {
  width: 100%;
}

.v-data-table__wrapper {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
}

.v-data-table table,
.v-simple-table table {
  min-width: 100%;
  width: max-content;
}

.v-data-table .v-progress-linear {
  min-width: 40px;
  max-width: 60px;
}

.v-data-table .v-chip {
  max-width: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v-list {
  overflow-x: hidden !important;
}

.v-list-item__title,
.v-list-item__subtitle {
  white-space: normal !important;
  word-break: break-word;
}

.v-simple-table {
  width: 100%;
  overflow-x: auto;
  display: block;
}

.v-simple-table table {
  min-width: 600px;
}

.v-progress-linear {
  max-width: 100%;
}

.v-progress-circular {
  margin: 0 auto;
}

.v-snackbar {
  max-width: 90vw;
  left: 5vw !important;
  right: 5vw !important;
}

.v-dialog {
  max-width: 95vw !important;
  margin: 16px;
}

.v-dialog .v-card {
  max-height: 90vh;
  overflow-y: auto;
}

img,
svg,
canvas,
video,
audio,
iframe {
  max-width: 100%;
  height: auto;
}

* {
  box-sizing: border-box;
}

body {
  overflow-x: hidden;
  width: 100%;
  position: relative;
}

/* ===== MOBILE STYLES (ALL CONSOLIDATED) ===== */
@media (max-width: 600px) {
  /* General */
  .v-card-text {
    padding: 12px !important;
  }
  
  .v-card-actions {
    padding: 8px !important;
  }
  
  .text-h3 {
    font-size: 2rem !important;
  }
  
  .text-h4 {
    font-size: 1.8rem !important;
  }
  
  .text-h5 {
    font-size: 1.3rem !important;
  }
  
  .text-h6 {
    font-size: 1rem !important;
  }
  
  /* Tabs - CRITICAL FIX FOR ICONS */
  .v-tabs {
    width: 100%;
    overflow-x: auto !important;
    overflow-y: hidden !important;
  }
  
  .v-tabs-bar {
    height: 48px !important;
  }
  
  .v-tab {
    min-width: auto !important;
    padding: 0 12px !important;
  }
  
  /* Hide tab labels, keep all icons visible */
  .tab-label {
    display: none !important;
  }
  
  /* Ensure ALL tab icons show properly */
  .v-tab .v-icon {
    display: inline-flex !important;
    margin-right: 0 !important;
    font-size: 20px !important;
    opacity: 1 !important;
    visibility: visible !important;
  }
  
  /* Specifically ensure folder-tree icon shows */
  .v-tab .v-icon.mdi-folder-tree {
    display: inline-flex !important;
  }
  
  /* Tables */
  .v-data-table {
    font-size: 12px;
  }
  
  .v-data-table td,
  .v-data-table th {
    padding: 8px 4px !important;
  }
  
  .v-data-table table {
    min-width: 500px;
  }
  
  .responsive-table table {
    min-width: 400px;
  }
  
  .v-data-table th:nth-child(4),
  .v-data-table td:nth-child(4),
  .v-data-table th:nth-child(5),
  .v-data-table td:nth-child(5) {
    display: none;
  }
  /* Search */
    .v-menu__content {
    left: 0 !important;
    right: 0 !important;
    width: 100vw !important;
    max-width: 100vw !important;
  }

  .search-results-card {
    width: 100vw !important;
    max-width: 100vw !important;
  }
  
  /* Chips */
  .v-chip {
    height: 20px;
    font-size: 9px;
  }
  
  .v-chip.x-small {
    height: 16px;
    font-size: 8px;
  }
  
  /* Progress */
  .v-progress-linear {
    margin: 4px 0;
  }
  
  /* Touch targets */
  .v-btn--small {
    min-height: 36px;
    min-width: 36px;
  }
  
  .v-list-item {
    min-height: 44px;
  }
  
  .v-treeview-node__root {
    min-height: 44px;
  }

  /* Add to your existing styles */
.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.theme--dark .border-bottom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.error.lighten-4 {
  background-color: #ffcdd2 !important;
}

.warning.lighten-4 {
  background-color: #fff9c4 !important;
}

.theme--dark .error.lighten-4 {
  background-color: #b71c1c !important;
}

.theme--dark .warning.lighten-4 {
  background-color: #f57f17 !important;
}
  
  .v-expansion-panel-header {
    min-height: 48px;
  }
  
  /* File Type */
  .type-label {
    max-width: 80px;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .percentage-bar {
    width: 60px;
  }
  
  .percentage-text {
    font-size: 10px;
  }
  
  /* Largest Files */
  .largest-file-item {
    padding: 8px 4px !important;
  }
  
  .rank-avatar {
    min-width: 40px !important;
    margin-right: 8px !important;
  }

  /* Add to your style section */
.share-menu {
  border-radius: 8px;
  overflow: hidden;
}

.share-menu .v-list-item {
  min-height: 40px;
}

.share-menu .v-list-item__icon {
  margin-right: 12px;
}

.share-menu .v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.theme--dark .share-menu .v-list-item:hover {
  background-color: rgba(255, 255, 255, 0.04);
}

/* Screenshot capture preparation */
.health-metrics-section {
  transition: all 0.3s ease;
}

/* Loading state for share button */
.v-btn--loading.ml-2 {
  opacity: 0.7;
}
  
  .rank-avatar-inner {
    width: 32px !important;
    height: 32px !important;
  }
  
  .file-name {
    font-size: 13px;
    white-space: normal;
    word-break: break-word;
    line-height: 1.3;
    max-width: 120px;
  }
  
  .file-extension-chip {
    display: inline-block;
    margin-top: 2px;
  }
  
  .file-details {
    font-size: 11px;
    white-space: normal;
  }
  
  .rank-chip {
    display: none;
  }
  
  /* Statistics */
  .stat-card {
    padding: 8px !important;
  }
  
  .stat-card .text-h6 {
    font-size: 1rem !important;
  }
  
  .size-range-item .d-flex {
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .range-label {
    min-width: 70px !important;
    font-size: 11px;
  }
  
  .progress-container {
    min-width: 80px !important;
  }
  
  .range-count {
    min-width: 60px !important;
    font-size: 10px;
    text-align: right;
  }
  
  .health-metric {
    margin-bottom: 12px;
  }
  
  .health-circular {
    width: 40px !important;
    height: 40px !important;
  }
  
  .metric-name {
    font-size: 10px;
  }
  
  /* Structure */
  .structure-header {
    flex-direction: column;
    align-items: flex-start !important;
  }
  
  .structure-actions {
    width: 100%;
    justify-content: space-between;
    margin-top: 8px;
  }
  
  .action-btn {
    flex: 1;
  }
  
  .structure-stat {
    padding: 6px !important;
  }
  
  .structure-stat .text-h6 {
    font-size: 1rem !important;
  }
  
  .responsive-treeview {
    font-size: 12px;
  }
  
  .v-treeview-node__label {
    white-space: normal;
    word-break: break-word;
    line-height: 1.3;
  }
  
  .tree-chips {
    margin-left: 0;
    margin-top: 4px;
    width: 100%;
  }
  
  .lines-chip, .files-chip, .complexity-chip {
    margin-left: 0 !important;
    margin-right: 4px;
  }
  
  .file-size {
    font-size: 10px;
  }
  
  /* Charts */
  .chart-container {
    height: 200px;
  }
  
  .line-chart {
    height: 180px;
  }
  
  /* Complexity */
  .complexity-stat-card {
    padding: 6px !important;
  }
  
  .complexity-stat-card .text-h6 {
    font-size: 1rem !important;
  }
  
  .complexity-table th,
  .complexity-table td {
    padding: 8px 4px !important;
    font-size: 11px;
  }
  
  .file-name-cell {
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
  }
  
  .nesting-bar {
    width: 30px !important;
  }
  
  .language-complexity-table th,
  .language-complexity-table td {
    padding: 8px 4px !important;
    font-size: 11px;
  }
  
  .language-complexity-table th:nth-child(3),
  .language-complexity-table td:nth-child(3) {
    display: none;
  }
  
  .language-complexity-table .v-chip {
    height: 20px;
    font-size: 10px;
    padding: 0 6px;
  }
  
  .language-name {
    max-width: 60px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  /* Duplicates */
  .duplicate-header {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 4px;
  }
  
  .duplicate-original {
    font-size: 13px;
    word-break: break-word;
    width: 100%;
  }
  
  .duplicate-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-left: 0 !important;
  }
  
  .duplicate-chips .v-chip {
    margin-left: 0 !important;
  }
  
  .duplicate-files-list .v-list-item {
    padding: 4px 0;
  }
  
  .duplicate-file-name {
    font-size: 11px;
    word-break: break-word;
  }
  
  .duplicate-summary {
    flex-wrap: wrap;
  }
  
  /* Language */
  .language-stats-table th,
  .language-stats-table td {
    padding: 6px 2px !important;
    font-size: 10px;
  }
  
  .language-stats-table .v-chip {
    height: 20px;
    font-size: 9px;
  }
  
  .import-group-header {
    padding: 0 8px;
    font-size: 12px;
  }
  
  .import-name {
    font-size: 11px;
    word-break: break-word;
  }
  
  .todo-item {
    padding: 4px 0;
  }
  
  .todo-comment {
    font-size: 11px;
    word-break: break-word;
    line-height: 1.3;
  }
  
  .todo-location {
    font-size: 9px;
  }
  
  /* Dependencies */
  .dependency-stat-card {
    padding: 6px !important;
  }
  
  .dependency-stat-card .text-h6 {
    font-size: 1rem !important;
  }
  
  .graph-toggle {
    flex-wrap: wrap;
  }
  
  .graph-toggle .v-btn {
    min-width: 50px;
    font-size: 11px;
  }
  
  .dependency-tree .v-treeview-node__label {
    font-size: 11px;
  }
  
  .dependency-name {
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Search Results Styling */
.search-result-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: background-color 0.2s ease;
}

.search-result-item:hover {
  background-color: rgba(25, 118, 210, 0.04);
  cursor: pointer;
}

.search-result-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.search-result-path {
  color: rgba(0, 0, 0, 0.6);
  font-size: 11px;
  display: flex;
  align-items: center;
}

.match-preview {
  background-color: rgba(0, 0, 0, 0.02);
  padding: 4px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 10px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.match-preview strong {
  background-color: #ffeb3b;
  color: #000;
  padding: 0 2px;
  border-radius: 2px;
}

.highlight-file {
  background-color: rgba(255, 235, 59, 0.3) !important;
  transition: background-color 0.5s ease;
}

.theme--dark .search-result-item:hover {
  background-color: rgba(255, 255, 255, 0.04);
}

.theme--dark .search-result-path {
  color: rgba(255, 255, 255, 0.6);
}

.theme--dark .match-preview {
  background-color: rgba(255, 255, 255, 0.02);
  color: #aaa;
}

.theme--dark .match-preview strong {
  background-color: #f57f17;
  color: #fff;
}

/* Pagination styling */
.v-pagination {
  margin: 8px 0;
}

.v-pagination__item {
  font-size: 12px;
  height: 28px;
  width: 28px;
}
  
  .dependents-chip {
    font-size: 8px;
    height: 16px;
  }
  
  .dependency-list-item {
    padding: 8px 4px !important;
  }
  
  .dependency-list-title {
    font-size: 12px;
    white-space: normal;
    word-break: break-word;
    line-height: 1.3;
  }
  
  .dependency-list-subtitle {
    font-size: 10px;
    white-space: normal;
  }
  
  .matrix-container {
    overflow-x: auto;
  }
  
  .dependency-matrix {
    min-width: 300px;
  }
  
  .matrix-header, .matrix-row-label {
    font-size: 9px;
    max-width: 60px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .matrix-cell {
    padding: 4px 2px !important;
  }
  
  .matrix-empty {
    font-size: 10px;
  }
  
  .circular-item {
    padding: 4px 0;
  }
  
  .circular-path {
    font-size: 11px;
    word-break: break-word;
    line-height: 1.3;
  }
  
  /* Action Buttons */
  .action-buttons {
    padding: 8px 4px !important;
  }
  
  .action-btn {
    margin: 2px !important;
    min-width: 40px;
  }
  
  .action-btn .v-icon {
    margin: 0 !important;
  }
  
  .v-card-actions {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .v-card-actions .v-btn {
    margin: 0 !important;
    min-width: 120px;
    flex: 1 1 auto;
  }
  
  .v-card-actions.justify-center.pa-4.flex-wrap .v-btn {
    margin: 0 !important;
    width: calc(50% - 4px);
  }
  
  /* Text truncation */
  .text-truncate {
    max-width: 150px;
  }
  
  /* Dialog */
  .v-dialog {
    margin: 8px;
    width: 95vw !important;
  }
  
  .code-preview {
    max-width: 100%;
    overflow-x: auto;
    font-size: 10px;
    white-space: pre-wrap;
    word-break: break-word;
  }
  
  /* File size distribution */
  .d-flex.align-center.mb-1 span:first-child {
    width: 100% !important;
    margin-bottom: 4px;
  }
  
  .d-flex.align-center.mb-1 .v-progress-linear {
    width: calc(100% - 120px) !important;
  }
  
  .d-flex.align-center.mb-1 span:last-child {
    width: 100px !important;
    text-align: right;
  }
}

/* ===== TABLET STYLES ===== */
@media (max-width: 960px) {
  .v-simple-table th:nth-child(4),
  .v-simple-table td:nth-child(4),
  .v-simple-table th:nth-child(5),
  .v-simple-table td:nth-child(5),
  .v-simple-table th:nth-child(6),
  .v-simple-table td:nth-child(6),
  .v-simple-table th:nth-child(7),
  .v-simple-table td:nth-child(7) {
    display: none;
  }
}
</style>